import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Chip,
  Button,
  Card,
  Tooltip,
  Badge,
  Container,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Grid,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import {
  ExpandMore,
  AssignmentTurnedIn,
  CheckCircle,
  Send,
  Lightbulb,
  FilterList,
  CloudUpload,
  Close,
  Save,
  Refresh,
  NavigateNext,
  Info,
  Visibility,
} from "@mui/icons-material";
import {
  getDAQMSSubCategories,
  getDAQMSFields,
  getDAQMSStandards,
  getDAQMSCategories,
  getDAQMSCategoryNames,
  getDAQMSQuestions,
  getNCStatements,
  getImplementationData,
  getTemplatesData,
  saveAssessmentRemark,
  getAssessmentRemarks,
  getDAQMSEvidence,
  getNCChecklist,
  // saveEvidence,
  // getEvidence,
} from "../../../services/apiCalibration";
import s3Utils from '../../../utils/s3Utils';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../Admin/ListFormManagement.css';
import {
  decryptText,
  decryptAppId,
  encryptText,
  encryptAppId,
} from "../../../utils/encryption";
import FileUploadComponent from "../../../components/FileUploadComponent";
import axios from "axios";
import PageTitle from '../../../components/PageTitle';
import useAssessmentTracking from '../../../hooks/useAssessmentTracking';
import { LocationWarningDialog, HourlyAttendanceDialog } from '../../../components/AssessmentTrackingDialogs';
import PreviewReportPDFViewer from '../../../components/PreviewReportPDFViewer';


// Table Row Component
const TableRowComponent = ({ requirement, ncSelections, remarks, onNcChange, onRemarkChange, onSubmit, isSubmitted, getSubmitTooltip, getSubmitButtonText, getNcOptionsForRequirement, getImplementationForClause, getTemplatesForClause, getExistingRemarkForClause, hasExistingRemark, handleEvidenceModalOpen, hasEvidence, getEvidenceForClause, isChecklistCompleted, handleOpenFeedbackDialog, handleOpenPdfViewer, hasNCChecklistForClause, handleOpenNCChecklistDialog }) => {
  const ncOptions = getNcOptionsForRequirement(requirement);
  const selectedNcItems = ncSelections.get(requirement.id) || new Set();
  const remarkValue = remarks.get(requirement.id) || "";
  const implementationText = getImplementationForClause(requirement.ClauseNo || requirement.clauseNo);
  const templates = getTemplatesForClause(requirement.ClauseNo || requirement.clauseNo);
  const submitted = isSubmitted(requirement.id);
  const clauseNo = requirement.ClauseNo || requirement.clauseNo || requirement.DisplayName;
  const hasNCChecklist = hasNCChecklistForClause(clauseNo);
  
  // Debug checkbox state
  if (ncOptions.length > 0 && selectedNcItems.size > 0) {
    console.log('🔍 TableRow Debug - Checkbox state for', requirement.id, ':', {
      clauseNo,
      ncOptionsIds: ncOptions.map(opt => opt.id),
      selectedNcItems: Array.from(selectedNcItems),
      itemHasSelections: selectedNcItems.size > 0
    });
  }
  
  
  return (
    <TableRow
      sx={{
        backgroundColor: submitted ? "#f8fff8" : "inherit",
        "&:hover": {
          backgroundColor: submitted ? "#f0f8f0" : "#f5f5f5",
        },
        transition: "background-color 0.2s ease",
      }}
    >
      <TableCell sx={{ minWidth: 300 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
          {requirement.StandardStatement || requirement.statement || "No statement available"}
        </Typography>
        {implementationText && (
          <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
            <Typography variant="caption">
              <strong>Implementation:</strong> {implementationText}
            </Typography>
          </Alert>
        )}
        {templates.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: "bold" }}>
              Document for Compliance:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {templates.map((template, idx) => (
                <Chip
                  key={idx}
                  label={template.FormatName}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 250 }}>
        {(() => {
          const clauseNo = requirement.ClauseNo || requirement.clauseNo;
          const existingRemark = getExistingRemarkForClause(clauseNo);
          const hasExisting = hasExistingRemark(clauseNo);
          const hasNoNcOptions = ncOptions.length === 0;
          const evidenceImages = getEvidenceForClause(clauseNo);
          
          // Don't show remarks field if no NC options are available
          if (hasNoNcOptions) {
            return null;
          }
          
          return (
            <Box>
              {/* Evidence Images Section */}
              {evidenceImages.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2', display: 'block', mb: 1 }}>
                    Evidence ({evidenceImages.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {evidenceImages.map((evidence, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            border: '2px solid #1976d2',
                            boxShadow: 2,
                          },
                        }}
                        onClick={() => {
                          if (evidence.signedUrl && (evidence.signedUrl.toLowerCase().includes('.pdf') || evidence.fileName?.toLowerCase().endsWith('.pdf'))) {
                            handleOpenPdfViewer(evidence.signedUrl, evidence.fileName || 'Evidence Document');
                          } else {
                            window.open(evidence.signedUrl, '_blank');
                          }
                        }}
                      >
                        <img
                          src={evidence.signedUrl}
                          alt={`Evidence ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23f0f0f0" width="80" height="80"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="sans-serif" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <Tooltip title={evidence.fileName}>
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              bgcolor: 'rgba(0, 0, 0, 0.6)',
                              color: 'white',
                              fontSize: '0.65rem',
                              p: 0.5,
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {idx + 1}
                          </Box>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Remarks Section */}
              {hasExisting && existingRemark ? (
                <Alert 
                  severity="info" 
                  sx={{ 
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #2196f3"
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    Existing Remark:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {existingRemark.Remark}
                  </Typography>
                </Alert>
              ) : (
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenFeedbackDialog(requirement)}
                    disabled={submitted || isChecklistCompleted}
                    startIcon={<Lightbulb />}
                    fullWidth
                    sx={{
                      textTransform: "none",
                      borderRadius: 1,
                      py: 1,
                      borderColor: remarkValue ? "#1976d2" : "#ccc",
                      color: remarkValue ? "#1976d2" : "#666",
                      fontWeight: remarkValue ? "bold" : "normal",
                      "&:hover": {
                        borderColor: "#1976d2",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    {remarkValue ? "View/Edit Remark/Observation/Suggestion" : "Add Remark/Observation/Suggestion on content of checklist for NABL"}
                  </Button>
                  {remarkValue && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        mt: 0.5, 
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {remarkValue.substring(0, 50)}{remarkValue.length > 50 ? "..." : ""}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          );
        })()}
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        {ncOptions.length > 0 ? (
          <FormGroup>
            {ncOptions.slice(0, 3).map((option, idx) => {
              const isChecked = selectedNcItems.has(option.id);
              const clauseNo = requirement.ClauseNo || requirement.clauseNo;
              const hasEvidenceData = hasEvidence(option.text, clauseNo);
              
              // Debug checkbox state for first few options
              if (idx === 0) {
                console.log('🔍 Checkbox render debug for', clauseNo, ':', {
                  optionId: option.id,
                  optionText: option.text,
                  selectedNcItems: Array.from(selectedNcItems),
                  hasOptionId: selectedNcItems.has(option.id),
                  isChecked
                });
              }
              
              return (
                <Box key={option.id || idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={(e) => onNcChange(requirement.id, option.id, e.target.checked)}
                        disabled={submitted || isChecklistCompleted}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        {option.text}
                      </Typography>
                    }
                    sx={{ margin: 0, flex: 1 }}
                  />
                  {isChecked && (
                    <Button
                      variant="contained"
                      size="small"
                      color={hasEvidenceData ? "success" : "error"}
                      onClick={() => handleEvidenceModalOpen(option.text, option.id, clauseNo)}
                      startIcon={hasEvidenceData ? <CheckCircle /> : <CloudUpload />}
                      disabled={submitted}
                      sx={{
                        ml: 1,
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 2,
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {hasEvidenceData ? 'View Details' : 'Upload Evidence'}
                    </Button>
                  )}
                </Box>
              );
            })}
            {ncOptions.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{ncOptions.length - 3} more options
              </Typography>
            )}
          </FormGroup>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No NC options available
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

const SubSectionComponent = ({ subSection, expandedAccordions, handleAccordionChange, questionsPerSection, initializeQuestionLimit, loadMoreQuestions, loadAllQuestions, ncSelections, remarks, handleNcChange, handleRemarkChange, handleSubmit, isSubmitted, getSubmitTooltip, getSubmitButtonText, getNcOptionsForRequirement, getImplementationForClause, getTemplatesForClause, getExistingRemarkForClause, hasExistingRemark, mainSectionId, handleEvidenceModalOpen, hasEvidence, getEvidenceForClause, isChecklistCompleted, handleOpenFeedbackDialog, handleOpenPdfViewer, hasNCChecklistForClause, handleOpenNCChecklistDialog }) => {
  const sectionId = `${mainSectionId}_${subSection.id}`;
  const currentLimit = questionsPerSection.get(sectionId) || 3;
  
  const filteredAndSortedQuestions = !subSection.questions ? [] : subSection.questions.filter((question) => {
    const questionClause = question.ClauseNo || question.clauseNo || question.clause;
    const subsectionClause = subSection.clause;
    if (!questionClause || !subsectionClause) return false;
    return questionClause.toString() === subsectionClause.toString();
  });

  const totalQuestions = filteredAndSortedQuestions.length;
  const visibleQuestions = filteredAndSortedQuestions.slice(0, currentLimit);
  const hasMoreQuestions = totalQuestions > currentLimit;

  // Initialize question limit if not already done
  useEffect(() => {
    if (!questionsPerSection.has(sectionId) && totalQuestions > 0) {
      initializeQuestionLimit(sectionId, totalQuestions);
    }
  }, [sectionId, totalQuestions, questionsPerSection, initializeQuestionLimit]);

  return (
    <Card
      elevation={2}
      sx={{
        m: 2,
        borderRadius: 3,
        transition: "all 0.2s ease",
        "&:hover": {
          elevation: 4,
        },
      }}
    >
      <Accordion
        expanded={expandedAccordions.has(subSection.id)}
        onChange={handleAccordionChange(subSection.id)}
        sx={{
          boxShadow: "none",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMore
              sx={{
                color: expandedAccordions.has(subSection.id) ? "#fff" : "#666",
                transition: "color 0.2s ease",
              }}
            />
          }
          sx={{
            background: expandedAccordions.has(subSection.id)
              ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
              : "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
            borderRadius: "12px 12px 0 0",
            minHeight: 64,
            transition: "all 0.2s ease",
            "&:hover": {
              background: expandedAccordions.has(subSection.id)
                ? "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)"
                : "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Lightbulb
              sx={{
                color: expandedAccordions.has(subSection.id)
                  ? "#fff"
                  : "#ff9800",
                mr: 2,
                transition: "color 0.2s ease",
              }}
            />
            <Typography
              variant="body1"
              fontWeight="medium"
              color={expandedAccordions.has(subSection.id) ? "#fff" : "#333"}
              sx={{
                fontSize: "1.1rem",
                transition: "color 0.2s ease",
              }}
            >
              {subSection.clause} {subSection.title}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            <Table
              sx={{
                borderCollapse: "separate",
                borderSpacing: 0,
                "& .MuiTableCell-root": {
                  borderRight: "1px solid #e0e0e0",
                  borderBottom: "1px solid #e0e0e0",
                  "&:last-child": {
                    borderRight: "none",
                  },
                },
              }}
            >
              {totalQuestions > 0 && (
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& .MuiTableCell-head": {
                        fontWeight: "bold",
                        fontSize: "0.8rem",
                        color: "#1976d2",
                        py: 1,
                      },
                    }}
                  >
                    <TableCell>Clause No.</TableCell>
                    <TableCell>Standard Statement</TableCell>
                    <TableCell>Compliance Requirement</TableCell>
                    <TableCell>Non-Compliance</TableCell>
                    <TableCell>
                      <b>Remark/Observation/Suggestion on checklist content for NABL</b>
                      <br />
                      {/* <span
                        style={{
                          fontSize: "12px",
                          fontStyle: "italic",
                          color: "#666",
                        }}
                      >
                        (This will not be considered as part of the report)
                      </span> */}
                    </TableCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {visibleQuestions.map((question) => (
                  <TableRowComponent
                    key={question.id || question.QuestionId}
                    requirement={question}
                    ncSelections={ncSelections}
                    remarks={remarks}
                    onNcChange={handleNcChange}
                    onRemarkChange={handleRemarkChange}
                    onSubmit={handleSubmit}
                    isSubmitted={isSubmitted}
                    getSubmitTooltip={getSubmitTooltip}
                    getSubmitButtonText={getSubmitButtonText}
                    getNcOptionsForRequirement={getNcOptionsForRequirement}
                    getImplementationForClause={getImplementationForClause}
                    getTemplatesForClause={getTemplatesForClause}
                    getExistingRemarkForClause={getExistingRemarkForClause}
                    hasExistingRemark={hasExistingRemark}
                    handleEvidenceModalOpen={handleEvidenceModalOpen}
                    hasEvidence={hasEvidence}
                    getEvidenceForClause={getEvidenceForClause}
                    isChecklistCompleted={isChecklistCompleted}
                    handleOpenFeedbackDialog={handleOpenFeedbackDialog}
                    handleOpenPdfViewer={handleOpenPdfViewer}
                    hasNCChecklistForClause={hasNCChecklistForClause}
                    handleOpenNCChecklistDialog={handleOpenNCChecklistDialog}
                  />
                ))}

                {hasMoreQuestions && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            loadMoreQuestions(sectionId, totalQuestions)
                          }
                          startIcon={<ExpandMore />}
                        >
                          Load 5 More Questions
                        </Button>

                        {totalQuestions - currentLimit > 5 && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() =>
                              loadAllQuestions(sectionId, totalQuestions)
                            }
                            sx={{ ml: 1 }}
                          >
                            Load All ({totalQuestions - currentLimit} remaining)
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {totalQuestions > 3 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{
                        textAlign: "center",
                        py: 1,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Showing {Math.min(currentLimit, totalQuestions)} of{" "}
                        {totalQuestions} questions for better performance
                        {currentLimit >= totalQuestions &&
                          totalQuestions > 3 && (
                            <span
                              style={{ color: "#4caf50", fontWeight: "bold" }}
                            >
                              {" "}
                              • All questions loaded
                            </span>
                          )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

const Checklist = ({ data, onUpdate, fields = [], sectionType }) => {
  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();

  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar notification
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  const location = useLocation();

  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log("📍 Using decrypted orgId from URL query:", decryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  // Function to get labType from URL query parameters with decryption
  const getLabTypeFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedLabType = searchParams.get("labType");
    if (encryptedLabType) {
      try {
        const decryptedLabType = decryptText(encryptedLabType);
        console.log(
          "📍 Using decrypted labType from URL query:",
          decryptedLabType
        );
        return decryptedLabType;
      } catch (error) {
        return encryptedLabType;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  // Function to get standard from URL query parameters with decryption
  const getStandardFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedStandard = searchParams.get("standard");
    if (encryptedStandard) {
      try {
        const decryptedStandard = decryptText(encryptedStandard);
        console.log(
          "📍 Using decrypted standard from URL query:",
          decryptedStandard
        );
        return decryptedStandard;
      } catch (error) {
        return encryptedStandard;
      }
    }
    return null;
  };
  const getUserTypeFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedUserType = searchParams.get("userType");
    if (encryptedUserType) {
      try {
        const decryptedUserType = decryptText(encryptedUserType);
        console.log(
          "📍 Using decrypted userType from URL query:",
          decryptedUserType
        );
        return decryptedUserType;
      } catch (error) {
        return encryptedUserType;
      }
    }
    return null;
  };
  const getMedicalImagingFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedMedicalImaging = searchParams.get("medicalImaging");
    if (encryptedMedicalImaging) {
      try {
        const decryptedMedicalImaging = decryptText(encryptedMedicalImaging);
        console.log(
          "📍 Using decrypted medicalImaging from URL query:",
          decryptedMedicalImaging
        );
        return decryptedMedicalImaging;
      } catch (error) {
        return encryptedMedicalImaging;
      }
    }
    return null;
  };
  const handleApplicationChecklist = () => {
  const url =
    "/main/assessor/nc-application" +
    "?appId=" + encodeURIComponent(encryptAppId(appId)) +
    "&orgId=" + encodeURIComponent(encryptText(orgId)) +
    "&assessmentId=" + encodeURIComponent(encryptText(assessmentId)) +
    "&assessorId=" + encodeURIComponent(encryptText(assessorId));

  window.open(url, "_blank"); // ✅ open in new tab
};

  const getOrgId = () => {
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getAssessmentId = () => {
    const queryAssessmentId = getAssessmentIdFromQuery();
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    return null;
  };

  const getLabType = () => {
    const queryLabType = getLabTypeFromQuery();
    if (queryLabType) {
      return queryLabType;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return null;
  };

  const getStandard = () => {
    const queryStandard = getStandardFromQuery();
    if (queryStandard) {
      return queryStandard;
    }
    return null;
  };

  const getUserType = () => {
    const queryUserType = getUserTypeFromQuery();
    if (queryUserType) {
      return queryUserType;
    }
    return null;
  };
  const getMedicalImaging = () => {
    const queryMedicalImaging = getMedicalImagingFromQuery();
    if (queryMedicalImaging) {
      return queryMedicalImaging;
    }
    return null;
  };

  const [orgId, setOrgId] = useState(getOrgId());
  const [appId, setAppId] = useState(getAppId());
  const [assessmentId, setAssessmentId] = useState(getAssessmentId());
  const [labType, setLabType] = useState(getLabType());
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [standard, setStandard] = useState(getStandard());
  const [userType, setUserType] = useState(getUserType());
  const [medicalImaging, setMedicalImaging] = useState(getMedicalImaging());

  // Dynamic field and standard configuration
  const [assessmentConfig, setAssessmentConfig] = useState({
    field: labType === "Medical" ? "Medical" : labType === "Calibration" ? "Calibration" : labType === "Testing" ? "Testing" : "",
    standard: standard,
  });

  // Filter categories based on userType
  const filterCategoriesByUserType = (categories, userTypeValue) => {
    if (!categories || !categories.length) return categories;

    const userTypeNum = parseInt(userTypeValue);
    console.log("🔍 Filtering categories for userType:", userTypeNum);

    // Define allowed DisIds for each userType
    const allowedDisIdsMedical = {
      1: [
        "4",
        "5",
        "6",
        "7",
        "8",
        ...(medicalImaging === "1" ? ["9"] : []),
        "10",
      ],
      3: [
        "4",
        "5",
        "6",
        "7",
        "8",
        ...(medicalImaging === "1" ? ["9"] : []),
        "10",
      ],
      2: ["4", "6", "7", ...(medicalImaging === "1" ? ["9"] : []), "10"],
      4: ["4", "6", "7", ...(medicalImaging === "1" ? ["9"] : []), "10"],
    };

    const allowedDisIdsTesting = {
      1: [
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
      ],
      3: [
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
      ],
      2: ["4", "6", "7", "9"],
      4: ["4", "6", "7", "9"],
    };

    const allowedIds = labType === "Medical" ? allowedDisIdsMedical[userTypeNum] : allowedDisIdsTesting[userTypeNum];

    if (!allowedIds) {
      console.log(
        "⚠️ No filter defined for userType:",
        userTypeNum,
        "- showing all categories"
      );
      return categories;
    }

    const filtered = categories.filter((category) => {
      const disId = String(category.DisId || category.disId || "");
      const isAllowed = allowedIds.includes(disId);

      if (!isAllowed) {
        console.log(
          `🚫 Filtering out category: ${category.CatName} (DisId: ${disId})`
        );
      }

      return isAllowed;
    });

    console.log(
      `✅ Filtered ${categories.length} categories to ${filtered.length} based on userType ${userTypeNum}`
    );
    return filtered;
  };

  console.log("Query Parameters:", { appId, orgId, assessmentId });
  const [formData] = useState({
    ...data,
  });
  const [submittedItems, setSubmittedItems] = useState(new Set());
  const [expandedAccordions, setExpandedAccordions] = useState(new Set());
  const [ncSelections, setNcSelections] = useState(new Map()); // Maps requirement ID to Set of selected NC items
  const [remarks, setRemarks] = useState(new Map()); // Maps requirement ID to remarks text

  // DAQMS Sub Categories state
  const [tabValue, setTabValue] = useState(0); // 0 for Requirements, 1 for DAQMS Sub Categories, 2 for DAQMS Categories
  const [daqmsData, setDaqmsData] = useState([]);
  const [daqmsLoading, setDaqmsLoading] = useState(false);
  const [daqmsFields, setDaqmsFields] = useState([]);
  const [daqmsStandards, setDaqmsStandards] = useState([]);
  const [daqmsFilters, setDaqmsFilters] = useState({
    field: assessmentConfig.field, // Use dynamic field
    standard: "",
    catId: "",
    isActive: 1,
  });
  const [daqmsError, setDaqmsError] = useState("");

  // DAQMS Categories state
  const [daqmsCategoryData, setDaqmsCategoryData] = useState([]);
  const [daqmsCategoryLoading, setDaqmsCategoryLoading] = useState(false);
  const [daqmsCategories, setDaqmsCategories] = useState([]);
  const [daqmsSubCategories, setDaqmsSubCategories] = useState([]);
  const [daqmsQuestions, setDaqmsQuestions] = useState([]);
  const [daqmsCategoryFilters, setDaqmsCategoryFilters] = useState({
    field: assessmentConfig.field, // Use dynamic field
    standard: "",
    displayId: "",
    disId: "",
  });
  const [daqmsCategoryError, setDaqmsCategoryError] = useState("");

  // Assessment tracking hook for location and hourly attendance
  const {
    locationWarningOpen,
    distanceMessage,
    attendanceCheckDialogOpen,
    attendanceCheckMessage,
    setLocationWarningOpen,
    setAttendanceCheckDialogOpen,
    checkHourlyAttendance,
  } = useAssessmentTracking({
    assessorId,
    appId,
    orgId,
  });

  // Dynamic Requirements state (for first tab)
  const [dynamicRequirements, setDynamicRequirements] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsError, setRequirementsError] = useState("");
  const [isDataFetched, setIsDataFetched] = useState(false); // Flag to prevent multiple fetches

  // Lazy loading states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [categorySubcategories, setCategorySubcategories] = useState(new Map()); // Cache subcategories by category
  const [subcategoryQuestions, setSubcategoryQuestions] = useState(new Map()); // Cache questions by subcategory

  // NC Statements state
  const [ncStatements, setNcStatements] = useState([]);
  const [ncStatementsLoading, setNcStatementsLoading] = useState(false);
  const [ncStatementsError, setNcStatementsError] = useState("");
  const [isNcStatementsFetched, setIsNcStatementsFetched] = useState(false);

  // Implementation Data state
  const [implementationData, setImplementationData] = useState([]);
  const [implementationLoading, setImplementationLoading] = useState(false);
  const [isImplementationFetched, setIsImplementationFetched] = useState(false);

  // Templates Data state
  const [templatesData, setTemplatesData] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [isTemplatesFetched, setIsTemplatesFetched] = useState(false);

  // Existing Remarks state
  const [existingRemarks, setExistingRemarks] = useState(new Map()); // Maps clause/itemId to existing remark data
  const [existingRemarksLoading, setExistingRemarksLoading] = useState(false);
  const [isExistingRemarksFetched, setIsExistingRemarksFetched] =
    useState(false);

  // Evidence Modal state
  const [evidenceModal, setEvidenceModal] = useState({
    open: false,
    ncItem: "",
    ncId: "", // NC statement ID
    clauseNo: "",
    notes: "",
    file: null,
    imagePreview: null,
    uploadStatus: null,
    selectedImages: [], // Array of selected image URLs from DAQMS Evidence
    selectedDocuments: [], // Array of selected document URLs (PDF/Excel)
  });
  const [uploading, setUploading] = useState(false);
  const [evidenceData, setEvidenceData] = useState(new Map()); // Maps NC item to evidence data
  const fileInputRef = useRef(null);

  // Completion confirmation dialog state
  const [completionDialog, setCompletionDialog] = useState({
    open: false,
    loading: false,
  });

  // Checklist completion status state
  const [isChecklistCompleted, setIsChecklistCompleted] = useState(false);
  const [completionDate, setCompletionDate] = useState(null);
  const [checkingCompletion, setCheckingCompletion] = useState(true);

  // Data loading completion state
  const [isDataFullyLoaded, setIsDataFullyLoaded] = useState(false);
  const [initialDataLoadAttempts, setInitialDataLoadAttempts] = useState(0);

  // Debug: Log when isChecklistCompleted changes
  useEffect(() => {
    console.log('🔄 isChecklistCompleted state changed:', isChecklistCompleted);
  }, [isChecklistCompleted]);

  // Feedback Dialog state
  const [feedbackDialog, setFeedbackDialog] = useState({
    open: false,
    requirementId: null,
    actualQuestionId: null,
    clauseNo: "",
    standardStatement: "",
    currentFeedback: "",
  });

  // PDF Viewer state
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: "",
    title: "Document Viewer",
  });

  // NC Checklist state
  const [ncChecklistData, setNcChecklistData] = useState([]);
  const [ncChecklistLoading, setNcChecklistLoading] = useState(false);
  const [ncChecklistDialog, setNcChecklistDialog] = useState({
    open: false,
    clauseNo: "",
    matchedRecords: [],
  });

  // DAQMS Evidence state
  const [daqmsEvidenceData, setDaqmsEvidenceData] = useState(new Map()); // Maps clauseNo to array of evidence
  const [daqmsEvidenceLoading, setDaqmsEvidenceLoading] = useState(false);
  const [isDaqmsEvidenceFetched, setIsDaqmsEvidenceFetched] = useState(false);

  // Performance optimization states
  const [visibleRequirements, setVisibleRequirements] = useState(10); // Show only first 5 initially
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchFilter, setSearchFilter] = useState(""); // Add search filter to reduce visible items
  const [debouncedSearchFilter, setDebouncedSearchFilter] = useState(""); // Debounced search
  const [isInitialized, setIsInitialized] = useState(false); // Prevent multiple initializations
  const [questionsPerSection, setQuestionsPerSection] = useState(new Map()); // Track visible questions per section

  // Debounce search filter to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchFilter(searchFilter);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchFilter]);

  // Initialize questions display limit
  const initializeQuestionLimit = (sectionId, totalQuestions) => {
    setQuestionsPerSection((prev) => {
      const newMap = new Map(prev);
      if (!newMap.has(sectionId)) {
        newMap.set(sectionId, Math.min(3, totalQuestions)); // Show max 3 questions initially
      }
      return newMap;
    });
  };

  const loadAllQuestions = (sectionId, totalQuestions) => {
    setQuestionsPerSection((prev) => {
      const newMap = new Map(prev);
      newMap.set(sectionId, totalQuestions); // Show all questions
      console.log(
        `Loading ALL questions for ${sectionId}: showing all ${totalQuestions} questions`
      );
      return newMap;
    });
    
    // Trigger checkbox binding for all newly revealed questions after a short delay
    setTimeout(() => {
      console.log('🔄 Triggering checkbox binding after loading all questions...');
      fetchSavedChecklist();
    }, 200);
  };

  const loadMoreQuestions = (sectionId, totalQuestions) => {
    setQuestionsPerSection((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(sectionId) || 3;
      // Always load exactly 5 more, but don't exceed total
      const newLimit = Math.min(current + 5, totalQuestions);
      newMap.set(sectionId, newLimit);
      console.log(
        `✅ Loading more questions for ${sectionId}: ${current} → ${newLimit} (total: ${totalQuestions})`
      );
      return newMap;
    });
    
    // Trigger checkbox binding for newly revealed questions after a short delay
    setTimeout(() => {
      console.log('🔄 Triggering checkbox binding after loading more questions...');
      fetchSavedChecklist();
    }, 200);
  };

  // Define NC options for specific requirements only (fallback - will be replaced by dynamic data)
  const ncOptions = {};

  // Dynamic NC options based on fetched NC statements
  const getNcOptionsForRequirement = (questionOrRequirement) => {
      // Try to get clause number from question or requirement object
      let clauseNo = null;

      if (typeof questionOrRequirement === "string") {
        console.log(
          "getNcOptionsForRequirement: Input is a string, using as clause number:",
          questionOrRequirement
        );
        // If it's a string (requirement ID), use it as clause number
        clauseNo = questionOrRequirement;
      } else if (
        questionOrRequirement &&
        typeof questionOrRequirement === "object"
      ) {
        // If it's a question object, extract clause number from various possible properties
        clauseNo =
          questionOrRequirement.ClauseNo ||
          questionOrRequirement.clauseNo ||
          questionOrRequirement.clause ||
          questionOrRequirement.DisplayName ||
          questionOrRequirement.displayName;
      }

      if (!clauseNo) {
        console.warn(
          "⚠️ getNcOptionsForRequirement: No clause number found for",
          questionOrRequirement
        );
        return [];
      }

      // Normalize the clause number for better matching
      const normalizedClauseNo = clauseNo.toString().trim();

      // First try to find NC statements matching the clause number
      const dynamicOptions = ncStatements
        .filter((statement) => {
          const statementClause = (
            statement.clauseno ||
            statement.ClauseNo ||
            statement.clause ||
            ""
          )
            .toString()
            .trim();
          const isMatch = statementClause === normalizedClauseNo;

          if (isMatch) {
            console.log(
              `✅ Found NC statement match: ${statementClause} = ${normalizedClauseNo}`
            );
          }

          return isMatch;
        })
        .map((statement) => {
          // Return an object with both ID and text
          return {
            id: String(statement.Id || statement.id), // Convert to string for consistency
            text: statement.Name ||
              statement.name ||
              statement.Statement ||
              statement.statement ||
              statement.text ||
              statement.StatementText
          };
        })
        .filter(option => option.id && option.text); // Remove any null/undefined values

      // If we found dynamic options, use them
      if (dynamicOptions.length > 0) {
        console.log(
          `✅ Found ${dynamicOptions.length} dynamic NC options for clause ${normalizedClauseNo}:`,
          dynamicOptions.slice(0, 2)
        );
        return dynamicOptions;
      }

      // Fallback to static options if no dynamic options found (convert to same format)
      const staticOptions = (ncOptions[normalizedClauseNo] || []).map(text => ({
        id: text,
        text: text
      }));
      if (staticOptions.length > 0) {
        console.log(
          `📋 Using ${staticOptions.length} static NC options for clause ${normalizedClauseNo}`
        );
      } else {
        console.log(`❌ No NC options found for clause ${normalizedClauseNo}`);
      }

      return staticOptions;
  };

  // Dynamic requirements built from DAQMS data (lazy loaded)
  const requirements = (() => {
    console.log("Building requirements with:", {
      categoriesCount: daqmsCategories.length,
      subcategoriesCount: daqmsSubCategories.length,
      questionsCount: daqmsQuestions.length,
    });

    // If no categories loaded, return empty
    if (!daqmsCategories.length) {
      console.log("No categories loaded yet");
      return [];
    }

    // Sort categories by clause number
    const sortedCategories = daqmsCategories.sort((a, b) => {
      const clauseA = String(
        a.DisplayId || a.displayId || a.DisId || a.disId || "0"
      );
      const clauseB = String(
        b.DisplayId || b.displayId || b.DisId || b.disId || "0"
      );
      const numA = parseFloat(clauseA);
      const numB = parseFloat(clauseB);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return clauseA.localeCompare(clauseB);
    });

    // Build requirements structure
    const result = sortedCategories
      .map((category) => {
        const categoryId =
          category.CategoryId ||
          category.categoryId ||
          category.Id ||
          category.id;
        const categoryName =
          category.CatName ||
          category.categoryName ||
          category.CategoryName ||
          category.Name ||
          category.name ||
          "Unknown Category";
        const displayId =
          category.DisplayId ||
          category.displayId ||
          category.DisId ||
          category.disId ||
          categoryId;

        if (!categoryId) {
          return null;
        }

        // Get subcategories for this category (only if they've been loaded)
        const categorySubcats = daqmsSubCategories.filter((subcat) => {
          const subCatId = subcat.CatId || subcat.catId;
          return subCatId === categoryId;
        });

        // Sort subcategories by clause number
        // const sortedSubcategories = categorySubcats.sort((a, b) => {
        //   const clauseA = String(a.SdisId || a.DisplayId || a.DisId || "0");
        //   const clauseB = String(b.SdisId || b.DisplayId || b.DisId || "0");
        //   const numA = parseFloat(clauseA);
        //   const numB = parseFloat(clauseB);
        //   if (!isNaN(numA) && !isNaN(numB)) {
        //     return numA - numB;
        //   }
        //   return clauseA.localeCompare(clauseB);
        // });
        const sortedSubcategories = [...categorySubcats].sort((a, b) => {
        const clauseA = String(a.SdisId || a.DisplayId || a.DisId || "0");
        const clauseB = String(b.SdisId || b.DisplayId || b.DisId || "0");
        const partsA = clauseA.split(".").map(Number);
        const partsB = clauseB.split(".").map(Number);
        if (partsA[0] !== partsB[0]) {
        return partsA[0] - partsB[0];
        }
       return (partsA[1] || 0) - (partsB[1] || 0);
      });

        return {
          id: categoryId.toString(),
          categoryId: categoryId,
          clause: displayId ? displayId.toString() : categoryId.toString(),
          title: categoryName,
          field: category.Field,
          standard: category.Standard,
          requirements: sortedSubcategories
            .map((subcat) => {
              const subCategoryId = subcat.SdisId;
              const subcatId = subcat.Id || subcat.id;
              const subCategoryName =
                subcat.SubCategory || "Unknown Subcategory";
              const subDisplayId =
                subcat.DisplayId ||
                subcat.displayId ||
                subcat.DisId ||
                subcat.disId ||
                subCategoryId;

              // Get questions for this subcategory (only if they've been loaded)
              const subcategoryQuestionsList = daqmsQuestions.filter(
                (question) => {
                  const questionSubCatId =
                    question.QSubCatId ||
                    question.SubCatId ||
                    question.qSubCatId ||
                    question.subCatId ||
                    question.subcategoryId ||
                    question.SubcategoryId;
                  return questionSubCatId === subcatId;
                }
              );

              // Sort questions by clause number
              const sortedQuestions = subcategoryQuestionsList.sort((a, b) => {
                const clauseA = String(
                  a.DisplayName ||
                    a.displayName ||
                    a.ClauseNo ||
                    a.clauseNo ||
                    "0"
                );
                const clauseB = String(
                  b.DisplayName ||
                    b.displayName ||
                    b.ClauseNo ||
                    b.clauseNo ||
                    "0"
                );

                // Try numeric comparison first
                const numA = parseFloat(clauseA);
                const numB = parseFloat(clauseB);

                if (!isNaN(numA) && !isNaN(numB)) {
                  return numA - numB;
                }

                // Fall back to string comparison
                return clauseA.localeCompare(clauseB);
              });

              return {
                id: subCategoryId ? subCategoryId.toString() : "unknown",
                subcatId: subcatId,
                clause: subDisplayId
                  ? subDisplayId.toString()
                  : subCategoryId
                  ? subCategoryId.toString()
                  : "unknown",
                title: subCategoryName,
                questions: sortedQuestions,
                requirements: [],
              };
            })
            .filter((subcat) => subcat.id !== "unknown"),
        };
      })
      .filter(Boolean);

    return result;
  })();

  // DAQMS Functions
  const fetchDAQMSData = async () => {
    setDaqmsLoading(true);
    setDaqmsError("");
    try {
      const response = await getDAQMSSubCategories(daqmsFilters);
      if (response.data.success) {
        setDaqmsData(response.data.data);
      } else {
        setDaqmsError(response.data.message || "Failed to fetch DAQMS data");
      }
    } catch (error) {
      console.error("Error fetching DAQMS data:", error);
      setDaqmsError("Failed to fetch DAQMS Sub Categories");
    } finally {
      setDaqmsLoading(false);
    }
  };

  // Fetch NC Statements for dynamic NC options
  const fetchNCStatements = async () => {
    // Prevent multiple simultaneous fetches
    if (ncStatementsLoading || isNcStatementsFetched) {
      console.log("🛑 fetchNCStatements: Skipped - already loading or fetched");
      return;
    }

    console.log("🚀 fetchNCStatements: Starting API call");

    try {
      setNcStatementsLoading(true);
      setNcStatementsError("");

      // Fetch NC statements with dynamic field and standard
      const response = await getNCStatements({
        field: assessmentConfig.field,
        standard: assessmentConfig.standard,
      });

      if (response.data.success) {
        const fetchedNCStatements = response.data.data;
        console.log(
          "✅ fetchNCStatements: Fetched",
          fetchedNCStatements.length,
          "NC statements"
        );
        setNcStatements(fetchedNCStatements);
        setIsNcStatementsFetched(true);
      } else {
        throw new Error("Failed to fetch NC statements");
      }
    } catch (error) {
      console.error("❌ fetchNCStatements: Error:", error);
      setNcStatementsError("Failed to load NC statements: " + error.message);
    } finally {
      setNcStatementsLoading(false);
    }
  };

  // Fetch Implementation Data from Compliance_Requirements table
  const fetchImplementationData = async () => {
    // Prevent multiple simultaneous fetches
    if (implementationLoading || isImplementationFetched) {
      console.log(
        "🛑 fetchImplementationData: Skipped - already loading or fetched"
      );
      return;
    }

    console.log("🚀 fetchImplementationData: Starting API call");

    try {
      setImplementationLoading(true);

      // Fetch implementation data with dynamic field and standard
      const response = await getImplementationData({
        field: assessmentConfig.field,
        standard: assessmentConfig.standard,
        isActive: 1,
      });

      if (response.data.success) {
        const fetchedImplementationData = response.data.data;
        console.log(
          "✅ fetchImplementationData: Fetched",
          fetchedImplementationData.length,
          "implementation records"
        );
        setImplementationData(fetchedImplementationData);
        setIsImplementationFetched(true);
      } else {
        throw new Error("Failed to fetch implementation data");
      }
    } catch (error) {
      console.error("❌ fetchImplementationData: Error:", error);
    } finally {
      setImplementationLoading(false);
    }
  };

  // Fetch Templates Data from ListFormats table
  const fetchTemplatesData = async () => {
    // Prevent multiple simultaneous fetches
    if (templatesLoading || isTemplatesFetched) {
      console.log(
        "🛑 fetchTemplatesData: Skipped - already loading or fetched"
      );
      return;
    }

    console.log("🚀 fetchTemplatesData: Starting API call");

    try {
      setTemplatesLoading(true);

      // Fetch templates data with dynamic field and standard
      const response = await getTemplatesData({
        field: assessmentConfig.field,
        standard: assessmentConfig.standard,
      });

      if (response.data.success) {
        const fetchedTemplatesData = response.data.data;
        console.log(
          "✅ fetchTemplatesData: Fetched",
          fetchedTemplatesData.length,
          "template records"
        );
        setTemplatesData(fetchedTemplatesData);
        setIsTemplatesFetched(true);
      } else {
        throw new Error("Failed to fetch templates data");
      }
    } catch (error) {
      console.error("❌ fetchTemplatesData: Error:", error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Fetch Existing Assessment Remarks
  const fetchExistingRemarks = async () => {
    // Prevent multiple simultaneous fetches
    if (existingRemarksLoading || isExistingRemarksFetched) {
      console.log(
        "🛑 fetchExistingRemarks: Skipped - already loading or fetched"
      );
      return;
    }

    console.log("🚀 fetchExistingRemarks: Starting API call with params:", {
      appId,
      orgId,
      assessmentId,
    });

    try {
      setExistingRemarksLoading(true);

      // Fetch existing remarks for this assessment
      const response = await getAssessmentRemarks({
        appId: appId,
        orgId: orgId,
        assessmentId: assessmentId,
      });

      if (response.data.success) {
        const fetchedRemarks = response.data.data;
        console.log(
          "✅ fetchExistingRemarks: Fetched",
          fetchedRemarks.length,
          "existing remarks"
        );

        // Convert array to Map for easy lookup by clause
        const remarksMap = new Map();
        fetchedRemarks.forEach((remark) => {
          if (remark.Clause) {
            remarksMap.set(remark.Clause, remark);
          }
        });

        setExistingRemarks(remarksMap);
        setIsExistingRemarksFetched(true);

        // Also populate the remarks state with existing values
        const newRemarksState = new Map(remarks);
        fetchedRemarks.forEach((remark) => {
          if (remark.Clause && remark.Remark) {
            // We'll need to map clause to itemId - for now use clause as key
            newRemarksState.set(remark.Clause, remark.Remark);
          }
        });
        setRemarks(newRemarksState);
      } else {
        throw new Error("Failed to fetch existing remarks");
      }
    } catch (error) {
      console.error("❌ fetchExistingRemarks: Error:", error);
    } finally {
      setExistingRemarksLoading(false);
    }
  };

  // Fetch existing evidence data
  // const fetchExistingEvidence = async () => {
  //   if (!appId || !orgId || !assessmentId) {
  //     return;
  //   }

  //   console.log("🚀 fetchExistingEvidence: Starting API call with params:", { appId, orgId, assessmentId });

  //   try {
  //     const response = await getEvidence({
  //       appId: appId,
  //       orgId: orgId,
  //       assessmentId: assessmentId
  //     });

  //     if (response.data.success) {
  //       const fetchedEvidence = response.data.data;
  //       console.log("✅ fetchExistingEvidence: Fetched", fetchedEvidence.length, "evidence records");

  //       // Convert array to Map for easy lookup by NC item and clause
  //       const evidenceMap = new Map();
  //       fetchedEvidence.forEach(evidence => {
  //         if (evidence.ncItem && evidence.clauseNo) {
  //           const evidenceKey = `${evidence.ncItem}_${evidence.clauseNo}`;
  //           evidenceMap.set(evidenceKey, {
  //             ncItem: evidence.ncItem,
  //             clauseNo: evidence.clauseNo,
  //             notes: evidence.notes,
  //             imageUrl: evidence.imageUrl,
  //             uploadDate: evidence.InsertDate,
  //             evidenceId: evidence.id
  //           });
  //         }
  //       });

  //       setEvidenceData(evidenceMap);

  //     } else {
  //       console.warn("Failed to fetch existing evidence:", response.data.message);
  //     }
  //   } catch (error) {
  //     console.error("❌ fetchExistingEvidence: Error:", error);
  //   }
  // }, [appId, orgId, assessmentId]);

  // Get implementation text for a specific clause number
  const getImplementationForClause = (clauseNo) => {
    if (!clauseNo || !implementationData.length) {
      return "";
    }

    const normalizedClauseNo = clauseNo.toString().trim();
    const implementation = implementationData.find((item) => {
      const itemClause = (item.clauseno || item.ClauseNo || "")
        .toString()
        .trim();
      return itemClause === normalizedClauseNo;
    });

    if (implementation) {
      console.log(`✅ Found implementation for clause ${normalizedClauseNo}`);
      // Use 'Name' field from your API response
      return (
        implementation.Name ||
        implementation.name ||
        implementation.Implementation ||
        implementation.implementation ||
        ""
      );
    }

    console.log(
      `❌ No implementation found for clause ${normalizedClauseNo}`
    );
    return "";
  };

  // Get templates for a specific clause number
  const getTemplatesForClause = (clauseNo) => {
      if (!clauseNo || !templatesData.length) {
        return [];
      }

      const normalizedClauseNo = clauseNo.toString().trim();
      const templates = templatesData.filter((item) => {
        const itemClause = (item.clauseno || item.ClauseNo || "")
          .toString()
          .trim();
        return itemClause === normalizedClauseNo;
      });

      if (templates.length > 0) {
        console.log(
          `✅ Found ${templates.length} template(s) for clause ${normalizedClauseNo}`
        );
        return templates;
      }

      console.log(`❌ No templates found for clause ${normalizedClauseNo}`);
      return [];
  };

  // Get existing remark for a specific clause or item
  const getExistingRemarkForClause = (clauseNo) => {
      if (!clauseNo || !existingRemarks.size) {
        return null;
      }

      const normalizedClauseNo = clauseNo.toString().trim();
      const existingRemark = existingRemarks.get(normalizedClauseNo);

      if (existingRemark) {
        console.log(
          `✅ Found existing remark for clause ${normalizedClauseNo}:`,
          existingRemark.Remark
        );
        return existingRemark;
      }

      return null;
  };

  // Check if a clause has existing remark
  const hasExistingRemark = (clauseNo) => {
    return getExistingRemarkForClause(clauseNo) !== null;
  };

  // Fetch DAQMS Evidence Data
  const fetchDAQMSEvidence = async () => {
    if (daqmsEvidenceLoading || isDaqmsEvidenceFetched) {
      console.log(
        "🛑 fetchDAQMSEvidence: Skipped - already loading or fetched"
      );
      return;
    }

    if (
      !appId ||
      !orgId ||
      !assessmentId ||
      !assessorId ||
      !labType ||
      !standard
    ) {
      console.log("⚠️ fetchDAQMSEvidence: Missing required parameters");
      return;
    }

    try {
      setDaqmsEvidenceLoading(true);

      const response = await getDAQMSEvidence({
        appId,
        orgId,
        assessmentId,
        assessorId,
        labTypeId: labType==="Medical" ? 3 : labType==="Calibration" ? 2 : 1, 
        standard,
      });

      if (response.data.success) {
        const fetchedEvidence = response.data.data;
        console.log(
          "✅ fetchDAQMSEvidence: Fetched",
          fetchedEvidence.length,
          "evidence records"
        );

        // Group evidence by clause number
        const evidenceByClause = new Map();

        for (const evidence of fetchedEvidence) {
          const clauseNo =
            evidence.Clauseno || evidence.clauseno || evidence.ClauseNo;
          const evidenceFileName = evidence.Evidence || evidence.evidence;

          if (clauseNo && evidenceFileName) {
            // Generate signed URL for the image
            let signedUrl = null;
            if (s3Utils.isJustFilename(evidenceFileName)) {
              signedUrl = await s3Utils.generateSignedUrlForFilename(
                evidenceFileName
              );
            }

            const evidenceItem = {
              id: evidence.id,
              fileName: evidenceFileName,
              signedUrl: signedUrl || evidenceFileName,
              insertedBy: evidence.Insertedby || evidence.insertedBy,
              isActive: evidence.isactive || evidence.isActive,
            };

            if (!evidenceByClause.has(clauseNo)) {
              evidenceByClause.set(clauseNo, []);
            }
            evidenceByClause.get(clauseNo).push(evidenceItem);
          }
        }

        setDaqmsEvidenceData(evidenceByClause);
        setIsDaqmsEvidenceFetched(true);
        console.log(
          "✅ Evidence grouped by clause:",
          Array.from(evidenceByClause.keys())
        );
      } else {
        console.warn("Failed to fetch DAQMS evidence:", response.data.message);
      }
    } catch (error) {
      console.error("❌ fetchDAQMSEvidence: Error:", error);
    } finally {
      setDaqmsEvidenceLoading(false);
    }
  };

  // Fetch NC Checklist data
  const fetchNCChecklist = async () => {
    if (!appId || !orgId || !assessorId || !assessmentId) {
      console.log("⚠️ Missing required parameters for fetching NC checklist");
      return;
    }

    try {
      setNcChecklistLoading(true);
      console.log("🚀 Fetching NC Checklist...", { appId, orgId, assessorId, assessmentId });

      const response = await getNCChecklist({
        appId,
        orgId,
        assessorId,
        assessmentId,
      });

      if (response.data.success) {
        const data = response.data.data || [];
        setNcChecklistData(data);
        console.log("✅ NC Checklist fetched:", data.length, "records");
        console.log("📋 Full NC Checklist data:", JSON.stringify(data, null, 2));
        
        // Log each record's clauseNo details
        data.forEach((record, index) => {
          const clauseNos = (record.clauseNo || "").split(",").map(c => c.trim());
          console.log(`   Record ${index + 1}: clauseNo="${record.clauseNo}" -> split into: [${clauseNos.map(c => `"${c}"`).join(", ")}]`);
        });
      } else {
        console.warn("Failed to fetch NC checklist:", response.data.message);
      }
    } catch (error) {
      console.error("❌ Error fetching NC checklist:", error);
    } finally {
      setNcChecklistLoading(false);
    }
  };

  // Check if a clause has matching NC records
  const hasNCChecklistForClause = (clauseNo) => {
    if (!clauseNo) {
      console.log("⚠️ hasNCChecklistForClause: No clauseNo provided");
      return false;
    }
    
    if (!ncChecklistData.length) {
      console.log("⚠️ hasNCChecklistForClause: ncChecklistData is empty");
      return false;
    }

    const normalizedClause = clauseNo.toString().trim();
    console.log(`🔍 Checking for NC matches - Input clause: "${normalizedClause}"`);
    
    const hasMatch = ncChecklistData.some(record => {
      const recordClauses = (record.clauseNo || "").split(",").map(c => c.trim());
      const isMatch = recordClauses.includes(normalizedClause);
      
      console.log(`   Checking record: clauseNo="${record.clauseNo}" -> split: [${recordClauses.map(c => `"${c}"`).join(", ")}] -> includes("${normalizedClause}")? ${isMatch}`);
      
      return isMatch;
    });
    
    console.log(`   ${hasMatch ? "✅ MATCH FOUND" : "❌ NO MATCH"} for clause "${normalizedClause}"`);
    
    return hasMatch;
  };

  // Get matching NC records for a clause
  const getNCChecklistForClause = (clauseNo) => {
    if (!clauseNo || !ncChecklistData.length) return [];

    const normalizedClause = clauseNo.toString().trim();
    
    return ncChecklistData.filter(record => {
      const recordClauses = (record.clauseNo || "").split(",").map(c => c.trim());
      return recordClauses.includes(normalizedClause);
    });
  };

  // Handle opening NC checklist dialog
  const handleOpenNCChecklistDialog = (clauseNo) => {
    const matchedRecords = getNCChecklistForClause(clauseNo);
    setNcChecklistDialog({
      open: true,
      clauseNo,
      matchedRecords,
    });
  };

  // Handle closing NC checklist dialog
  const handleCloseNCChecklistDialog = () => {
    setNcChecklistDialog({
      open: false,
      clauseNo: "",
      matchedRecords: [],
    });
  };

  // Get evidence images for a specific clause number
  const getEvidenceForClause = (clauseNo) => {
    if (!clauseNo || !daqmsEvidenceData.size) {
      return { images: [], documents: [] };
    }

    const normalizedClauseNo = clauseNo.toString().trim();
    const evidence = daqmsEvidenceData.get(normalizedClauseNo) || [];

    // Separate images from documents
    const images = evidence.filter(item => {
      const fileName = item.fileName || '';
      return fileName.match(/\.(jpg|jpeg|png|gif)$/i);
    });

    const documents = evidence.filter(item => {
      const fileName = item.fileName || '';
      return fileName.match(/\.(pdf|xlsx|xls)$/i);
    });

    if (images.length > 0 || documents.length > 0) {
      console.log(
        `✅ Found ${images.length} image(s) and ${documents.length} document(s) for clause ${normalizedClauseNo}`
      );
    }

    return { images, documents };
  };

  // Fetch saved checklist data and prefill the form
  const fetchSavedChecklist = async () => {
    if (!appId || !assessmentId || !assessorId) {
      console.log('⚠️ Missing required parameters for fetching checklist');
      return;
    }

    // Check if we have sufficient requirements built to proceed
    const totalQuestions = requirements.reduce((total, section) => 
      total + section.requirements.reduce((subTotal, subSection) => 
        subTotal + (subSection.questions ? subSection.questions.length : 0), 0), 0);
    
    if (requirements.length === 0 || totalQuestions === 0) {
      console.log('⚠️ Requirements not built yet or no questions loaded, skipping checkbox binding', {
        requirementsCount: requirements.length,
        totalQuestions
      });
      return;
    }

    console.log('🚀 Starting fetchSavedChecklist with complete data:', {
      requirementsCount: requirements.length,
      totalQuestions,
      appId,
      assessmentId, 
      assessorId
    });

    try {
      console.log('🚀 Fetching saved data (remarks from GetChecklist, checkboxes from GetNC)...', { appId, assessmentId, assessorId });

      // ============================================
      // STEP 1: Fetch REMARKS from GetChecklist API
      // ============================================
      const checklistUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetChecklist?appid=${appId}&assessmentid=${assessmentId}&assessorid=${assessorId}`;
      const checklistResponse = await fetch(checklistUrl);
      const checklistResult = await checklistResponse.json();

      // Handle both success formats (isstatus from .NET API and success from Node)
      const checklistData = checklistResult.success ? checklistResult.data?.checklist : (checklistResult.isstatus ? checklistResult.checklist : null);

      // ============================================
      // STEP 2: Fetch CHECKBOX VALUES from GetNC API
      // ============================================
      const ncUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetNCMain?appid=${appId}&assessmentid=${assessmentId}&assessorid=${assessorId}`;
      const ncResponse = await fetch(ncUrl);
      const ncResult = await ncResponse.json();

      // Handle both response formats - API returns result.nc array
      const ncData = ncResult.success ? ncResult.data : (ncResult.isstatus ? ncResult.nc : null);

      console.log('✅ GetChecklist response:', checklistData ? `${checklistData.length} remarks` : 'No data');
      console.log('✅ GetNC response:', ncData ? `${ncData.length} NC items` : 'No data');

      // Initialize state maps
      const newNcSelections = new Map();
      const newRemarks = new Map();
      const newSubmittedItems = new Set();

      // ============================================
      // STEP 3: Create NC mapping by questionItemId
      // ============================================
      const ncByQuestionId = new Map();

      if (ncData && Array.isArray(ncData) && ncData.length > 0) {
        console.log('🔍 Building NC checkbox mapping from GetNC data...', ncData);

        // Create a map of clause numbers to NC IDs for faster lookup
        const clauseToNcIds = new Map();
        ncData.forEach(nc => {
          const ncClause = (nc.clauseno || nc.ClauseNo || '').toString().trim();
          const ncId = String(nc.ncid || nc.NCID);
          
          if (ncClause && ncId) {
            if (!clauseToNcIds.has(ncClause)) {
              clauseToNcIds.set(ncClause, new Set());
            }
            clauseToNcIds.get(ncClause).add(ncId);
            console.log(`📝 Added NC mapping: clause ${ncClause} -> ncid ${ncId}`);
          }
        });

        console.log('📋 Clause to NC IDs mapping:', Array.from(clauseToNcIds.entries()));

        // Map NC items to their questionItemId based on clause numbers
        for (const section of requirements) {
          for (const subSection of section.requirements) {
            // Process questions
            if (subSection.questions) {
              for (let qIndex = 0; qIndex < subSection.questions.length; qIndex++) {
                const question = subSection.questions[qIndex];
                const questionItemId = `${subSection.id}_q_${qIndex}`;
                const questionClause = (question.ClauseNo || question.clauseNo || question.DisId || question.DisplayName || '').toString().trim();

                // Check if we have NC items for this clause
                if (questionClause && clauseToNcIds.has(questionClause)) {
                  const ncIds = clauseToNcIds.get(questionClause);
                  ncByQuestionId.set(questionItemId, new Set(ncIds));
                  console.log(`✅ Mapped NC checkboxes for itemId ${questionItemId}, clause ${questionClause}:`, Array.from(ncIds));
                } else if (questionClause) {
                  console.log(`❌ No NC items found for clause ${questionClause}`);
                }
              }
            }

            // Process requirements (fallback)
            if (subSection.requirements) {
              subSection.requirements.forEach(requirement => {
                const reqClause = (requirement.clause || requirement.ClauseNo || '').toString().trim();
                const reqItemId = requirement.id;

                // Check if we have NC items for this clause
                if (reqClause && clauseToNcIds.has(reqClause)) {
                  const ncIds = clauseToNcIds.get(reqClause);
                  ncByQuestionId.set(reqItemId, new Set(ncIds));
                  console.log(`✅ Mapped NC checkboxes for reqItemId ${reqItemId}, clause ${reqClause}:`, Array.from(ncIds));
                }
              });
            }
          }
        }

        console.log(`✅ NC mapping complete: ${ncByQuestionId.size} items have checkboxes`);
        console.log('🔍 Final NC selections mapping:', Array.from(ncByQuestionId.entries()));
      }

      // ============================================
      // STEP 4: Process REMARKS from GetChecklist
      // ============================================
      if (checklistData && checklistData.length > 0) {
        console.log('🔍 Processing remarks from GetChecklist...');

        checklistData.forEach(item => {
          const { qid, clauseno, remarks: itemRemarks } = item;
          console.log(`🔎 Processing remark: qid=${qid}, clauseno=${clauseno}`);

          let foundMatch = false;

          // Find the matching question/requirement by qid and clauseno
          for (const section of requirements) {
            for (const subSection of section.requirements) {
              // Check questions
              if (subSection.questions) {
                for (let qIndex = 0; qIndex < subSection.questions.length; qIndex++) {
                  const question = subSection.questions[qIndex];
                  const questionItemId = `${subSection.id}_q_${qIndex}`;

                  // Match primarily by qid (question Id)
                  if (question.Id?.toString() === qid?.toString()) {
                    foundMatch = true;
                    console.log(`✅ MATCH FOUND for remarks! Question ID: ${question.Id}, ItemId: ${questionItemId}`);

                    // Set remarks ONLY (no checkbox data from GetChecklist)
                    newRemarks.set(questionItemId, itemRemarks || '');
                    console.log(`✅ Loaded remark for question ${qid}:`, itemRemarks || '(empty)');

                    // Mark as submitted if has remarks
                    if (itemRemarks && itemRemarks.trim()) {
                      newSubmittedItems.add(questionItemId);
                    }

                    break;
                  }
                }
              }

              // Check requirements (fallback)
              if (subSection.requirements) {
                subSection.requirements.forEach(requirement => {
                  const reqClause = (requirement.clause || requirement.ClauseNo || '').toString().trim();

                  if (reqClause === clauseno?.toString().trim()) {
                    foundMatch = true;
                    console.log(`✅ MATCH FOUND for remarks! Requirement clause: ${reqClause}`);

                    // Set remarks ONLY (no checkbox data from GetChecklist)
                    newRemarks.set(requirement.id, itemRemarks || '');
                    console.log(`✅ Loaded remark for clause ${clauseno}:`, itemRemarks || '(empty)');

                    // Mark as submitted if has remarks
                    if (itemRemarks && itemRemarks.trim()) {
                      newSubmittedItems.add(requirement.id);
                    }
                  }
                });
              }
            }
          }

          if (!foundMatch) {
            console.warn(`⚠️ No match found for remark: qid=${qid}, clauseno=${clauseno}`);
          }
        });
      }

      // ============================================
      // STEP 5: Apply NC checkbox selections
      // ============================================
      ncByQuestionId.forEach((ncSet, itemId) => {
        newNcSelections.set(itemId, ncSet);
        // Mark as submitted if has NC selections
        if (ncSet.size > 0) {
          newSubmittedItems.add(itemId);
        }
      });

      console.log('📊 Final state summary:', {
        ncSelectionsCount: newNcSelections.size,
        remarksCount: newRemarks.size,
        submittedItemsCount: newSubmittedItems.size
      });

      // Debug log the actual NC selections data
      console.log('🔍 NC Selections being set:', Array.from(newNcSelections.entries()).map(([key, value]) => ({
        itemId: key,
        selectedNCIds: Array.from(value)
      })));

      // Update state
      setNcSelections(newNcSelections);
      setRemarks(newRemarks);
      setSubmittedItems(newSubmittedItems);

      console.log('✅ Data loaded: Remarks from GetChecklist, Checkboxes from GetNC');
      //showSnackbar('Assessment data loaded successfully', 'success');

    } catch (error) {
      console.error('❌ Error fetching saved data:', error);
      showSnackbar('Failed to load saved data', 'error');
    }
  };

  // Completion Dialog Functions
  const handleOpenCompletionDialog = () => {
    setCompletionDialog({ open: true, loading: false });
  };

  const handleCloseCompletionDialog = () => {
    if (!completionDialog.loading) {
      setCompletionDialog({ open: false, loading: false });
    }
  };

  const handleConfirmCompletion = async () => {
    setCompletionDialog((prev) => ({ ...prev, loading: true }));

    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Updatechecklist?assessorId=${assessorId}&assessmentId=${assessmentId}&appId=${appId}`;
      
      const response = await axios.put(url);

      if (response.data && response.data.isstatus) {
        showSnackbar(response.data.message || "Checklist completed successfully!", "success");
        setCompletionDialog({ open: false, loading: false });
        
        // Update checklist status to read-only
        setIsChecklistCompleted(true);
        setCompletionDate(response.data.message || new Date().toLocaleString());
      } else {
        showSnackbar("Failed to complete checklist. Please try again.", "error");
        setCompletionDialog((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error("Error completing checklist:", error);
      showSnackbar("An error occurred while completing checklist.", "error");
      setCompletionDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // Evidence Modal Functions
  const handleEvidenceModalOpen = useCallback(
    async (ncItemText, ncItemId, clauseNo) => {
      await fetchDAQMSEvidence();
      try {
        console.log('🚀 Fetching NC evidence data...', { appId, assessmentId, assessorId, clauseNo, ncItemId });

        const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetNC?appid=${appId}&assessmentid=${assessmentId}&assessorid=${assessorId}&ClauseNo=${clauseNo}&ncid=${ncItemId}`;
        const response = await fetch(apiUrl);
        const result = await response.json();

        console.log('📥 NC API Response:', result);

        // Handle both response formats - API returns result.nc array
        const ncData = result.success ? result.data : (result.isstatus ? result.nc : null);

        if (ncData && Array.isArray(ncData) && ncData.length > 0) {
          console.log('✅ NC evidence data fetched:', ncData);

          // Find the matching NC evidence by ncId
          const matchingEvidence = ncData.find(item => 
            item.ncid?.toString() === ncItemId?.toString() || 
            item.NCID?.toString() === ncItemId?.toString()
          );

          if (matchingEvidence) {
            console.log('✅ Found matching NC evidence:', matchingEvidence);
            
            // Parse document field (tilde-separated URLs)
            const documentUrls = (matchingEvidence.document || matchingEvidence.Document || '').split('~').filter(url => url.trim());
            console.log('📎 Document URLs:', documentUrls);
            
            // Separate PDF/Excel files from images
            let pdfExcelFile = null;
            const imageUrls = [];
            const selectedDocUrls = []; // Array to collect ALL PDF/Excel files
            
            // Use for...of to support await inside the loop
            for (const url of documentUrls) {
              const urlLower = url.toLowerCase();
              if (urlLower.endsWith('.pdf') || urlLower.endsWith('.xlsx') || urlLower.endsWith('.xls')) {
                // PDF or Excel file - use the first one for FileUploadComponent
                if (!pdfExcelFile) {
                  pdfExcelFile = { fileName: url, key: `live/${url}`, originalName: url };
                }
                // Add ALL PDF/Excel files to selectedDocuments
                selectedDocUrls.push(url);
              } else if (urlLower.match(/\.(jpg|jpeg|png)$/)) {
                // Image file - add to selectedImages
                if (s3Utils.isJustFilename(url)) {
                  const imageUrl = await s3Utils.generateSignedUrlForFilename(url);
                  imageUrls.push(imageUrl);
                } else {
                  // URL is already a full S3 URL
                  imageUrls.push(url);
                }
              }
            }
            
            console.log('PDF/Excel file:', pdfExcelFile);
            console.log('Image URLs:', imageUrls);
            console.log('Selected Document URLs:', selectedDocUrls);

            // Update local evidence data cache
            const evidenceKey = `${ncItemText}_${clauseNo}`;
            setEvidenceData(prevData => {
              const newMap = new Map(prevData);
              newMap.set(evidenceKey, {
                ncItem: ncItemText,
                ncId: ncItemId,
                clauseNo: clauseNo,
                notes: matchingEvidence.remarks || matchingEvidence.Remarks || '',
                imageUrl: matchingEvidence.document || matchingEvidence.Document || '',
                uploadDate: matchingEvidence.InsertDate || matchingEvidence.insertDate || new Date(),
              });
              return newMap;
            });
            
            // Open modal with parsed data
            setEvidenceModal({
              open: true,
              ncItem: ncItemText,
              ncId: ncItemId,
              clauseNo: clauseNo,
              notes: matchingEvidence.remarks || matchingEvidence.Remarks || '',
              file: pdfExcelFile,
              imagePreview: null,
              uploadStatus: null,
              selectedImages: imageUrls,
              selectedDocuments: selectedDocUrls,
            });
            return; // Exit early since we've set the modal
          } else {
            console.log('ℹ️ No matching NC evidence found for ncId:', ncItemId);
          }
        } else {
          console.log('ℹ️ No NC evidence data available for clause:', clauseNo);
        }

        // Open modal with default data (when no matching evidence found)
        setEvidenceModal({
          open: true,
          ncItem: ncItemText,
          ncId: ncItemId,
          clauseNo: clauseNo,
          notes: '',
          file: null,
          imagePreview: null,
          uploadStatus: null,
          selectedImages: [],
          selectedDocuments: [],
        });

      } catch (error) {
        console.error('❌ Error fetching NC evidence:', error);
        // Open modal with default data if API fails
        setEvidenceModal({
          open: true,
          ncItem: ncItemText,
          ncId: ncItemId,
          clauseNo: clauseNo,
          notes: '',
          file: null,
          imagePreview: null,
          uploadStatus: null,
          selectedImages: [],
          selectedDocuments: [],
        });
      }
    },
    [appId, assessmentId, assessorId]
  );

  const handleEvidenceModalClose = useCallback(() => {
    setEvidenceModal({
      open: false,
      ncItem: "",
      ncId: "",
      clauseNo: "",
      notes: "",
      file: null,
      imagePreview: null,
      uploadStatus: null,
      selectedImages: [],
      selectedDocuments: [],
    });
  }, []);

  const handleEvidenceFileChange = useCallback((fieldName, fileData) => {
    console.log("Evidence file selected:", fileData);
    // FileUploadComponent returns { fileName: "s3-url", originalName: "file.pdf" }
    // We need to store the file info for the upload process
    setEvidenceModal((prev) => ({
      ...prev,
      file: fileData, // Store the file data from FileUploadComponent
      imagePreview: null, // Clear image preview since we support PDFs/Excel now
      uploadStatus: null,
    }));
  }, []);

  const handleSaveEvidence = useCallback(async () => {
    if (!evidenceModal.ncItem || !evidenceModal.ncId || !evidenceModal.clauseNo) {
      setEvidenceModal(prev => ({
        ...prev,
        uploadStatus: 'Error: Missing required information'
      }));
      return;
    }

    if (!evidenceModal.file && !evidenceModal.notes.trim()) {
      setEvidenceModal(prev => ({
        ...prev,
        uploadStatus: 'Error: Please provide either an image or notes'
      }));
      return;
    }

    setUploading(true);
    setEvidenceModal(prev => ({
      ...prev,
      uploadStatus: null
    }));

    try {
      let documentUrls = [];

      // FileUploadComponent already uploads to S3, so we get the URL from file.fileName
      if (evidenceModal.file && evidenceModal.file.fileName) {
        documentUrls.push(evidenceModal.file.fileName); // This is the S3 URL from FileUploadComponent
        console.log('✅ Using uploaded file URL:', evidenceModal.file.fileName);
      }

      // Add selected images from DAQMS Evidence
      if (evidenceModal.selectedImages && evidenceModal.selectedImages.length > 0) {
        const fileNames = evidenceModal.selectedImages.map(url => url.split('/').pop());
        documentUrls.push(...fileNames);
        console.log('✅ Including selected images:', evidenceModal.selectedImages);
      }

      // Add selected documents (PDF/Excel) from DAQMS Evidence
      if (evidenceModal.selectedDocuments && evidenceModal.selectedDocuments.length > 0) {
        documentUrls.push(...evidenceModal.selectedDocuments);
        console.log('✅ Including selected documents:', evidenceModal.selectedDocuments);
      }

      // Join all URLs with tilde separator
      const documentUrl = documentUrls.join('~');
      console.log('📎 Final document URL string:', documentUrl);

      // Save NC evidence to database via API
      const payload = {
        ncid: String(evidenceModal.ncId),
        appid: String(appId || ''),
        assessmentid: String(assessmentId || ''),
        assessorid: String(assessorId || ''),
        orgid: String(orgId || ''),
        clauseno: String(evidenceModal.clauseNo),
        remarks: evidenceModal.notes.trim() || '',
        document: documentUrl,
        description: evidenceModal.ncItem || ''
      };

      console.log('📤 Saving NC evidence with payload:', payload);

      const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Savenc`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      // Handle both success formats (isstatus from .NET API and success from Node)
      if (result.success || result.isstatus) {
        // Update local evidence data
        const evidenceKey = `${evidenceModal.ncItem}_${evidenceModal.clauseNo}`;
        setEvidenceData(prev => {
          const newMap = new Map(prev);
          newMap.set(evidenceKey, {
            ncItem: evidenceModal.ncItem,
            ncId: evidenceModal.ncId,
            clauseNo: evidenceModal.clauseNo,
            notes: evidenceModal.notes,
            imageUrl: documentUrl,
            uploadDate: new Date(),
          });
          return newMap;
        });

        setEvidenceModal(prev => ({
          ...prev,
          uploadStatus: 'Evidence saved successfully!'
        }));

        showSnackbar('NC evidence saved successfully!', 'success');

        // Close modal after a short delay
        setTimeout(() => {
          handleEvidenceModalClose();
        }, 1500);

      } else {
        throw new Error(result.message || 'Failed to save NC evidence');
      }

    } catch (error) {
      console.error('❌ Error saving NC evidence:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save NC evidence';
      setEvidenceModal(prev => ({
        ...prev,
        uploadStatus: 'Error: ' + errorMsg
      }));
      showSnackbar(errorMsg, 'error');
    } finally {
      setUploading(false);
    }
  }, [evidenceModal, appId, assessmentId, assessorId, orgId, handleEvidenceModalClose, showSnackbar]);

  const hasEvidence = useCallback(
    (ncItem, clauseNo) => {
      return evidenceData.has(`${ncItem}_${clauseNo}`);
    },
    [evidenceData]
  );

  // Fetch DAQMS Categories and SubCategories for dynamic requirements
  const fetchDAQMSRequirementsData = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (requirementsLoading || isDataFetched) {
      console.log(
        "🛑 fetchDAQMSRequirementsData: Skipped - already loading or fetched"
      );
      return;
    }

    console.log(
      "🚀 fetchDAQMSRequirementsData: Starting API calls - ONLY CATEGORIES"
    );

    try {
      setRequirementsLoading(true);

      // Fetch ONLY categories on initial load
      const categoriesResponse = await getDAQMSCategories({
        field: assessmentConfig.field,
      });
      if (categoriesResponse.data.success) {
        // Filter categories based on userType before setting state
        const filteredCategories = filterCategoriesByUserType(
          categoriesResponse.data.data,
          userType
        );
        setDaqmsCategories(filteredCategories);
        console.log(
          "✅ Loaded",
          filteredCategories.length,
          "categories (filtered by userType:",
          userType,
          ")"
        );
      }

      setIsDataFetched(true);
      console.log(
        "✅ fetchDAQMSRequirementsData: Completed successfully (categories only)"
      );
    } catch (error) {
      console.error("❌ fetchDAQMSRequirementsData: Error:", error);
    } finally {
      setRequirementsLoading(false);
    }
  }, [requirementsLoading, isDataFetched, assessmentConfig.field, filterCategoriesByUserType, userType]);

  // Fetch subcategories for a specific category
  const fetchSubcategoriesForCategory = useCallback(
    async (categoryId, categoryName) => {
      // Check cache first
      if (categorySubcategories.has(categoryId)) {
        console.log(
          "✅ Using cached subcategories for category:",
          categoryName
        );
        return categorySubcategories.get(categoryId);
      }

      console.log(
        "🚀 Fetching subcategories for category:",
        categoryName,
        "ID:",
        categoryId
      );
      setLoadingSubcategories(true);

      try {
        const response = await getDAQMSSubCategories({
          field: assessmentConfig.field,
          catId: categoryId,
          isActive: 1,
        });

        if (response.data.success) {
          const subcategories = response.data.data;
          console.log(
            "✅ Loaded",
            subcategories.length,
            "subcategories for",
            categoryName
          );

          // Cache the subcategories
          setCategorySubcategories((prev) => {
            const newMap = new Map(prev);
            newMap.set(categoryId, subcategories);
            return newMap;
          });

          // Update global state
          setDaqmsSubCategories((prevSubs) => {
            // Merge with existing subcategories
            const existingIds = new Set(prevSubs.map((s) => s.Id || s.id));
            const newSubs = subcategories.filter(
              (s) => !existingIds.has(s.Id || s.id)
            );
            return [...prevSubs, ...newSubs];
          });

          return subcategories;
        }
      } catch (error) {
        console.error("❌ Error fetching subcategories:", error);
        return [];
      } finally {
        setLoadingSubcategories(false);
      }
    },
    [categorySubcategories, assessmentConfig.field]
  );

  // Apply NC selections to newly loaded questions
  const applyNCSelectionsToNewQuestions = useCallback(async (newQuestions, subcategoryId) => {
    console.log('� Re-applying NC selections after dynamic question loading...');
    
    // Trigger checkbox binding for dynamic loading scenarios  
    // Allow binding if we have basic data (categories and NC statements) even if not "fully loaded"
    const hasBasicDataForBinding = daqmsCategories.length > 0 && ncStatements.length > 0;
    
    if (isDataFullyLoaded || hasBasicDataForBinding) {
      setTimeout(() => {
        console.log('🔗 Applying checkbox binding to dynamically loaded questions...');
        fetchSavedChecklist();
      }, 200);
    } else {
      console.log('⚠️ Basic data not available yet for checkbox binding:', {
        hasCategories: daqmsCategories.length > 0,
        hasNcStatements: ncStatements.length > 0,
        isDataFullyLoaded
      });
    }
  }, [fetchSavedChecklist, isDataFullyLoaded, daqmsCategories, ncStatements]);

  // Fetch questions and related data for a specific subcategory
  const fetchQuestionsForSubcategory = useCallback(
    async (subcategoryId, subcategoryName, clauseNo) => {
      // Check cache first
      if (subcategoryQuestions.has(subcategoryId)) {
        console.log(
          "✅ Using cached questions for subcategory:",
          subcategoryName
        );
        return subcategoryQuestions.get(subcategoryId);
      }

      console.log(
        "🚀 Fetching questions for subcategory:",
        subcategoryName,
        "ID:",
        subcategoryId
      );
      setLoadingQuestions(true);

      try {
        // Fetch questions for this subcategory - API will filter by subCatId
        const questionsResponse = await getDAQMSQuestions({
          field: assessmentConfig.field,
          standard: assessmentConfig.standard,
          subCategoryId: subcategoryId, // Pass subcategory ID to API
        });

        if (questionsResponse.data.success) {
          const questions = questionsResponse.data.data;
          console.log(
            "✅ Loaded",
            questions.length,
            "questions for",
            subcategoryName
          );

          // Cache the questions
          setSubcategoryQuestions((prev) => {
            const newMap = new Map(prev);
            newMap.set(subcategoryId, questions);
            return newMap;
          });

          // Update global state
          setDaqmsQuestions((prevQuestions) => {
            // Merge with existing questions
            const existingIds = new Set(prevQuestions.map((q) => q.id || q.Id));
            const newQuestions = questions.filter(
              (q) => !existingIds.has(q.id || q.Id)
            );
            return [...prevQuestions, ...newQuestions];
          });

          // Apply existing NC selections to newly loaded questions
          await applyNCSelectionsToNewQuestions(questions, subcategoryId);

          // Additional trigger with longer delay to ensure requirements structure is updated
          setTimeout(() => {
            console.log('🔗 Additional checkbox binding trigger after subcategory questions loaded...');
            fetchSavedChecklist();
          }, 800);

          // Fetch related data for this subcategory's clause number
          if (clauseNo) {
            // Fetch NC Statements if not already fetched
            if (!isNcStatementsFetched) {
              await fetchNCStatements();
            }

            // Fetch Implementation Data if not already fetched
            if (!isImplementationFetched) {
              await fetchImplementationData();
            }

            // Fetch Templates Data if not already fetched
            if (!isTemplatesFetched) {
              await fetchTemplatesData();
            }

            // Fetch Existing Remarks if not already fetched
            if (!isExistingRemarksFetched) {
              await fetchExistingRemarks();
            }
          }

          return questions;
        }
      } catch (error) {
        console.error("❌ Error fetching questions:", error);
        return [];
      } finally {
        setLoadingQuestions(false);
      }
    },
    [
      subcategoryQuestions,
      assessmentConfig.field,
      assessmentConfig.standard,
      isNcStatementsFetched,
      isImplementationFetched,
      isTemplatesFetched,
      isExistingRemarksFetched,
      fetchNCStatements,
      fetchImplementationData,
      fetchTemplatesData,
      fetchExistingRemarks,
    ]
  );

  // Handle category click/expansion
  const handleCategoryExpand = useCallback(
    async (category) => {
      // Use categoryId (numeric ID)
      const categoryId = category.categoryId || category.Id || category.id;
      const categoryName =
        category.title ||
        category.CatName ||
        category.categoryName ||
        category.Name;

      console.log("📂 Category clicked:", categoryName, "ID:", categoryId);

      // Validate that categoryId is numeric
      if (!categoryId || isNaN(categoryId)) {
        console.error("❌ Invalid category ID:", categoryId);
        return;
      }

      setSelectedCategory(categoryId);

      // Fetch subcategories for this category
      await fetchSubcategoriesForCategory(categoryId, categoryName);
    },
    [fetchSubcategoriesForCategory]
  );

  // Handle subcategory click/expansion
  const handleSubcategoryExpand = useCallback(
    async (subcategory) => {
      // Use subcatId (numeric ID) instead of id (clause number string)
      const subcategoryId =
        subcategory.subcatId || subcategory.Id || subcategory.id;
      const subcategoryName =
        subcategory.title || subcategory.SubCategory || subcategory.subCategory;
      const clauseNo =
        subcategory.clause || subcategory.SdisId || subcategory.sdisId;

      console.log(
        "📄 Subcategory clicked:",
        subcategoryName,
        "ID:",
        subcategoryId
      );

      // Validate that subcategoryId is numeric
      if (!subcategoryId || isNaN(subcategoryId)) {
        console.error("❌ Invalid subcategory ID:", subcategoryId);
        return;
      }

      setSelectedSubCategory(subcategoryId);

      // Fetch questions and related data for this subcategory
      await fetchQuestionsForSubcategory(
        subcategoryId,
        subcategoryName,
        clauseNo
      );
      
      // Additional triggers for checkbox binding after subcategory expansion
      setTimeout(() => {
        console.log('🔗 Manual trigger: Checkbox binding after subcategory expansion (500ms)...');
        fetchSavedChecklist();
      }, 500);
      
      setTimeout(() => {
        console.log('🔗 Manual trigger: Checkbox binding after subcategory expansion (1000ms)...');
        fetchSavedChecklist();
      }, 1000);
    },
    [fetchQuestionsForSubcategory, fetchSavedChecklist]
  );

  const fetchDAQMSFields = useCallback(async () => {
    try {
      const response = await getDAQMSFields();
      if (response.data.success) {
        setDaqmsFields(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching DAQMS fields:", error);
    }
  }, []);

  const fetchDAQMSStandards = useCallback(async () => {
    try {
      const response = await getDAQMSStandards();
      if (response.data.success) {
        setDaqmsStandards(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching DAQMS standards:", error);
    }
  }, []);

  const handleDAQMSFilterChange = useCallback((filterName, value) => {
    setDaqmsFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  }, []);

  // DAQMS Categories Functions
  const fetchDAQMSCategoryData = useCallback(async () => {
    setDaqmsCategoryLoading(true);
    setDaqmsCategoryError("");
    try {
      const response = await getDAQMSCategories(daqmsCategoryFilters);
      if (response.data.success) {
        // Filter categories based on userType before setting state
        const filteredCategories = filterCategoriesByUserType(
          response.data.data,
          userType
        );
        setDaqmsCategoryData(filteredCategories);
        console.log(
          "✅ DAQMS Category data filtered:",
          filteredCategories.length,
          "categories (userType:",
          userType,
          ")"
        );
      } else {
        setDaqmsCategoryError(
          response.data.message || "Failed to fetch DAQMS Categories data"
        );
      }
    } catch (error) {
      console.error("Error fetching DAQMS Categories data:", error);
      setDaqmsCategoryError("Failed to fetch DAQMS Categories");
    } finally {
      setDaqmsCategoryLoading(false);
    }
  }, [daqmsCategoryFilters, filterCategoriesByUserType, userType]);

  const fetchDAQMSCategoryNames = useCallback(async () => {
    try {
      const response = await getDAQMSCategoryNames();
      if (response.data.success) {
        // Category names fetched successfully
      }
    } catch (error) {
      console.error("Error fetching DAQMS category names:", error);
    }
  }, []);

  const handleDAQMSCategoryFilterChange = useCallback((filterName, value) => {
    setDaqmsCategoryFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  }, []);

  // Build dynamic requirements from DAQMS data
  const buildDynamicRequirements = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (requirementsLoading) {
      console.log("🛑 buildDynamicRequirements: Skipped - already loading");
      return;
    }

    console.log("🚀 buildDynamicRequirements: Starting build process");

    setRequirementsLoading(true);
    setRequirementsError("");

    try {
      // Fetch categories first with dynamic field filter
      const categoriesResponse = await getDAQMSCategories({
        field: assessmentConfig.field,
      });
      if (!categoriesResponse.data.success) {
        throw new Error("Failed to fetch categories");
      }

      // Filter categories based on userType
      const categories = filterCategoriesByUserType(
        categoriesResponse.data.data,
        userType
      );
      console.log(
        `✅ Using ${categories.length} filtered categories (userType: ${userType})`
      );

      // Fetch ALL subcategories at once instead of making individual calls
      const allSubCategoriesResponse = await getDAQMSSubCategories({
        field: assessmentConfig.field, // Filter by dynamic field instead of individual catId
        isActive: 1,
      });

      if (!allSubCategoriesResponse.data.success) {
        throw new Error("Failed to fetch subcategories");
      }

      const allSubCategories = allSubCategoriesResponse.data.data;
      const dynamicReqs = [];

      // Group subcategories by category ID for efficient processing
      const subCategoriesByCategory = allSubCategories.reduce((acc, subCat) => {
        const catId = subCat.CatId || subCat.catId;
        if (!acc[catId]) {
          acc[catId] = [];
        }
        acc[catId].push(subCat);
        return acc;
      }, {});

      console.log(
        `Processing ${categories.length} categories with grouped subcategories (total: ${allSubCategories.length})`
      );

      // Sort categories by clause number
      const sortedCategories = categories.sort((a, b) => {
        const clauseA = String(a.DisplayId || a.DisId || "0");
        const clauseB = String(b.DisplayId || b.DisId || "0");

        // Try numeric comparison first
        const numA = parseFloat(clauseA);
        const numB = parseFloat(clauseB);

        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }

        // Fall back to string comparison
        return clauseA.localeCompare(clauseB);
      });

      // Process each category with its pre-grouped subcategories
      for (const category of sortedCategories) {
        const subCategories = subCategoriesByCategory[category.Id] || [];

        if (subCategories.length > 0) {
          // Sort subcategories by clause number
          const sortedSubCategories = subCategories.sort((a, b) => {
            const clauseA = String(
              a.SDisplayId || a.DisplayId || a.DisId || a.SdisId || "0"
            );
            const clauseB = String(
              b.SDisplayId || b.DisplayId || b.DisId || b.SdisId || "0"
            );

            // Try numeric comparison first
            const numA = parseFloat(clauseA);
            const numB = parseFloat(clauseB);

            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }

            // Fall back to string comparison
            return clauseA.localeCompare(clauseB);
          });

          // Build category structure
          const categoryStructure = {
            id: category.Id.toString(),
            clause: category.DisplayId || category.DisId.toString(),
            title: category.CatName,
            field: category.Field,
            standard: category.Standard,
            requirements: sortedSubCategories.map((subCat) => ({
              id: subCat.Id.toString(),
              clause: subCat.SDisplayId || subCat.SdisId.toString(),
              title: subCat.SubCategory,
              category: subCat.Category,
              requirements: [
                {
                  id: `${subCat.Id}_req`,
                  clause: subCat.SDisplayId || subCat.SdisId.toString(),
                  subclause: "",
                  requirement: subCat.SubCategory,
                  implementation: "",
                  templates: "",
                  compliance: "",
                  remark: "",
                },
              ],
            })),
          };

          dynamicReqs.push(categoryStructure);
        }
      }

      setDynamicRequirements(dynamicReqs);
    } catch (error) {
      console.error("Error building dynamic requirements:", error);
      setRequirementsError("Failed to load requirements data");
    } finally {
      setRequirementsLoading(false);
    }
  }, [requirementsLoading, assessmentConfig.field, filterCategoriesByUserType, userType]);

  const handleTabChange = useCallback(
    (event, newValue) => {
      // Prevent tab changes while loading
      if (requirementsLoading || daqmsLoading || daqmsCategoryLoading) {
        console.log("Tab change prevented: data is still loading");
        return;
      }

      setTabValue(newValue);
      if (newValue === 0 && requirements.length === 0) {
        // For first tab, fetch DAQMS requirements data
        fetchDAQMSRequirementsData();
      } else if (newValue === 0 && dynamicRequirements.length === 0) {
        buildDynamicRequirements();
      } else if (newValue === 1 && daqmsData.length === 0) {
        fetchDAQMSData();
      } else if (newValue === 2 && daqmsCategoryData.length === 0) {
        fetchDAQMSCategoryData();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      daqmsData.length,
      daqmsCategoryData.length,
      dynamicRequirements.length,
      requirements.length,
      requirementsLoading,
      daqmsLoading,
      daqmsCategoryLoading,
      // Functions excluded to prevent infinite loops
      // fetchDAQMSData,
      // fetchDAQMSCategoryData,
      // buildDynamicRequirements,
      // fetchDAQMSRequirementsData,
    ]
  );

  useEffect(() => {
    if (onUpdate) {
      onUpdate(formData);
    }
  }, [formData, onUpdate]);

  // Check if checklist is already completed
  useEffect(() => {
    const checkChecklistCompletion = async () => {
      if (!assessorId || !assessmentId || !appId) {
        console.log('⚠️ Missing parameters for checklist check:', { assessorId, assessmentId, appId });
        setCheckingCompletion(false);
        return;
      }

      try {
        const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Checkchecklist?assessorId=${assessorId}&assessmentId=${assessmentId}&appId=${appId}`;
        console.log('🔍 Checking checklist completion:', url);
        const response = await axios.get(url);
        console.log('📥 Checkchecklist API Response:', response.data);

        if (response.data && response.data.isstatus === true) {
          setIsChecklistCompleted(true);
          setCompletionDate(response.data.message);
          console.log('✅ Checklist is completed! Setting to read-only mode:', response.data.message);
        } else {
          setIsChecklistCompleted(false);
          setCompletionDate(null);
          console.log('📝 Checklist is not completed, edit mode enabled');
        }
      } catch (error) {
        console.error('❌ Error checking checklist completion:', error);
        setIsChecklistCompleted(false);
        setCompletionDate(null);
      } finally {
        setCheckingCompletion(false);
      }
    };

    checkChecklistCompletion();
  }, [assessorId, assessmentId, appId]);

  useEffect(() => {
    // Fetch DAQMS data for dynamic requirements on component mount
    if (!isDataFetched && !requirementsLoading) {
      // Use flag to prevent re-execution
      fetchDAQMSRequirementsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataFetched, requirementsLoading]); // Remove function dependency to prevent loops

  useEffect(() => {
    // Calculate progress based on submitted items
    const currentReqs = tabValue === 0 ? requirements : dynamicRequirements;
    const totalItems = currentReqs.reduce(
      (acc, section) =>
        acc +
        section.requirements.reduce(
          (subAcc, subSection) => subAcc + subSection.requirements.length,
          0
        ),
      0
    );
    const completedItems = submittedItems.size;
    // Progress calculation (not currently displayed but available for future use)
    const currentProgress =
      totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
    console.log("Assessment progress:", currentProgress + "%");
  }, [submittedItems, dynamicRequirements, requirements, tabValue]);

  // Initialize DAQMS data on component mount
  useEffect(() => {
    // Only run once on mount, ignore function dependencies to prevent loops
    if (!isInitialized) {
      setIsInitialized(true);
      fetchDAQMSFields();
      fetchDAQMSStandards();
      fetchDAQMSCategoryNames();
      fetchNCStatements(); // Fetch NC statements for dynamic NC options
      fetchImplementationData(); // Fetch implementation data
      fetchTemplatesData(); // Fetch templates data
      fetchExistingRemarks(); // Fetch existing assessment remarks
      fetchDAQMSEvidence(); // Fetch DAQMS evidence data
      fetchNCChecklist(); // Fetch NC checklist data
      // fetchExistingEvidence(); // Fetch existing evidence data
      // Load dynamic requirements for first tab by default
      buildDynamicRequirements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]); // Only depend on initialization flag

  // Fetch DAQMS data when filters change
  useEffect(() => {
    if (tabValue === 1) {
      // Use a flag or timeout to prevent rapid calls
      const timeoutId = setTimeout(() => {
        fetchDAQMSData();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daqmsFilters, tabValue]); // Remove function dependency

  // Fetch DAQMS Category data when filters change
  useEffect(() => {
    if (tabValue === 2) {
      // Use a flag or timeout to prevent rapid calls
      const timeoutId = setTimeout(() => {
        fetchDAQMSCategoryData();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daqmsCategoryFilters, tabValue]); // Remove function dependency

  useEffect(() => {
    // Build dynamic requirements when DAQMS field or standard filters change
    fetchSavedChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove function dependency

  // Watch for new questions being loaded and trigger checkbox binding
  useEffect(() => {
    if (daqmsQuestions.length > 0) {
      console.log('📋 DAQMS questions changed, triggering checkbox binding...', {
        questionsCount: daqmsQuestions.length
      });
      
      // Delay to ensure requirements structure is rebuilt
      const timer = setTimeout(() => {
        fetchSavedChecklist();
      }, 400);
      
      return () => clearTimeout(timer);
    }
  }, [daqmsQuestions.length]); // Trigger when question count changes

  // Track when all initial data is loaded for checkbox binding
  useEffect(() => {
    const hasCategories = daqmsCategories.length > 0;
    const hasSubcategories = daqmsSubCategories.length > 0;
    const hasQuestions = daqmsQuestions.length > 0;
    const hasRequirements = requirements.length > 0;
    const hasNcStatements = ncStatements.length > 0;
    
    // Check if we have the core data needed for building requirements
    const coreDataLoaded = hasCategories && hasSubcategories && hasRequirements;
    
    console.log('📊 Data loading status check:', {
      hasCategories,
      hasSubcategories, 
      hasQuestions,
      hasRequirements,
      hasNcStatements,
      coreDataLoaded,
      isDataFullyLoaded,
      attempts: initialDataLoadAttempts
    });
    
    // If core data is loaded and we haven't marked as fully loaded yet
    if (coreDataLoaded && !isDataFullyLoaded) {
      setIsDataFullyLoaded(true);
      console.log('✅ Core data fully loaded, will trigger checkbox binding...');
      
      // Trigger checkbox binding after a small delay
      setTimeout(() => {
        if (appId && assessmentId && assessorId) {
          console.log('🔗 Triggering checkbox binding now that all data is loaded...');
          fetchSavedChecklist();
        }
      }, 500);
    }
    
    // Increment attempts counter to prevent infinite loops
    setInitialDataLoadAttempts(prev => prev + 1);
    
  }, [daqmsCategories, daqmsSubCategories, daqmsQuestions, requirements, ncStatements, 
      isDataFullyLoaded, appId, assessmentId, assessorId, fetchSavedChecklist]);

  // Fetch saved checklist data when requirements are loaded (secondary trigger)
  useEffect(() => {
    // Only run if data is marked as fully loaded to avoid premature binding
    if (requirements.length > 0 && appId && assessmentId && assessorId && isDataFullyLoaded) {
      console.log('📋 Secondary trigger: Requirements changed after full data load:', {
        requirementsCount: requirements.length,
        totalQuestions: requirements.reduce((total, section) => 
          total + section.requirements.reduce((subTotal, subSection) => 
            subTotal + (subSection.questions ? subSection.questions.length : 0), 0), 0),
        appId,
        assessmentId,
        assessorId
      });
      
      // Small delay to ensure state is stable
      const timer = setTimeout(() => {
        fetchSavedChecklist();
      }, 300);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirements, daqmsQuestions, appId, assessmentId, assessorId, isDataFullyLoaded]); // Only trigger when data is fully loaded

  const handleSubmit = useCallback(
    async (itemId, questionId = null) => {
      
      try {
        // Get remark value for this item FIRST to check if it exists
        const remarkValue = remarks.get(itemId) || "";
        
        console.log(`📝 Checking remarks Map for itemId: ${itemId}`);
        console.log(`📝 Remark value found:`, remarkValue);
        console.log(`📝 Remarks Map size:`, remarks.size);
        console.log(`📝 All itemIds in remarks Map:`, Array.from(remarks.keys()));
        
        // Get clauseNo and qid from requirements structure
        let clauseNo = null;
        let qid = questionId; // Use the passed questionId (which is the API's Id field)
        
        for (const section of requirements) {
          for (const subSection of section.requirements) {
            // Check if it's a question in the subsection
            if (subSection.questions) {
              for (
                let qIndex = 0;
                qIndex < subSection.questions.length;
                qIndex++
              ) {
                const questionItemId = `${subSection.id}_q_${qIndex}`;
                if (questionItemId === itemId) {
                  const question = subSection.questions[qIndex];
                  clauseNo =
                    question.ClauseNo ||
                    question.clauseNo ||
                    question.DisplayName ||
                    "";
                  // Use the Id from API response if not already set
                  if (!qid) {
                    qid = question.Id || question.qid || question.id || questionItemId || "";
                  }
                  break;
                }
              }
            }
            // Check if it's a requirement in the subsection
            for (const requirement of subSection.requirements) {
              if (requirement.id === itemId) {
                clauseNo =
                  requirement.clause ||
                  requirement.ClauseNo ||
                  requirement.clauseNo ||
                  "";
                if (!qid) {
                  qid = requirement.qid || requirement.id || "";
                }
                break;
              }
            }
          }
        }

        // Prepare payload for savechecklist API
        // This API now ONLY saves remarks, NOT checkbox values
        const payload = {
          qid: String(qid ?? ""),
          appid: String(appId ?? ""),
          assessmentid: String(assessmentId ?? ""),
          assessorid: String(assessorId ?? ""),
          orgid: String(orgId ?? ""),
          clauseno: String(clauseNo ?? ""),
          nC_doc_id: "", // Always empty - checkboxes are saved via Savenc API
          remarks: remarkValue,
        };

        console.log("📤 Final payload to be sent:", payload);

        // Validate mandatory fields before API call (nC_doc_id is NOT required)
        const requiredFields = [
          "qid",
          "appid",
          "assessmentid",
          "assessorid",
          "orgid",
          "clauseno",
        ];
        for (const field of requiredFields) {
          if (
            payload[field] === "" ||
            payload[field] === undefined ||
            payload[field] === null
          ) {
            console.warn(`Mandatory field missing or empty: ${field}`);
            showSnackbar(`Mandatory field missing or empty: ${field}`, "error");
            return;
          }
        }

        const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Savechecklist`;
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (result.isstatus) {
          console.log(
            "✅ Remarks saved successfully:",
            result.message || result
          );
          const wasAlreadySubmitted = submittedItems.has(itemId);
          showSnackbar(
            wasAlreadySubmitted 
              ? "Remarks updated successfully!" 
              : "Remarks saved successfully!", 
            "success"
          );
          setSubmittedItems((prev) => new Set([...prev, itemId]));
        } else {
          console.error(
            "❌ Failed to save remarks:",
            result.message || result
          );
          showSnackbar(result.message || "Failed to save remarks", "error");
        }
        // Mark item as submitted
        console.log(`✅ Remarks saved for ${itemId}`);
      } catch (error) {
        console.error("❌ Error during submit:", error);
        showSnackbar("Error during submit", "error");
      }
    },
    [remarks, requirements, appId, orgId, assessmentId, assessorId, showSnackbar]
  );

  const handleRemarkChange = useCallback((itemId, value) => {
    setRemarks((prev) => {
      const newMap = new Map(prev);
      newMap.set(itemId, value);
      console.log(`✏️ Remark updated for ${itemId}:`, value);
      console.log(`📝 Total remarks in Map:`, newMap.size);
      return newMap;
    });
  }, []);

  // Feedback Dialog Handlers
  const handleOpenFeedbackDialog = useCallback((requirement) => {
    const itemId = requirement.id;
    const currentFeedback = remarks.get(itemId) || "";
    
    // Get the actual question ID and clause number
    const actualQuestionId = requirement.QuestionId || requirement.Id || requirement.id;
    const actualClauseNo = requirement.DisplayName || requirement.ClauseNo || requirement.clauseNo || "N/A";
    
    console.log('📝 Opening feedback dialog:', { 
      itemId, 
      actualQuestionId, 
      actualClauseNo,
      requirement 
    });
    
    setFeedbackDialog({
      open: true,
      requirementId: itemId,
      actualQuestionId: actualQuestionId,
      clauseNo: actualClauseNo,
      standardStatement: requirement.StandardStatement || requirement.statement || "No statement available",
      currentFeedback: currentFeedback,
    });
  }, [remarks]);

  const handleCloseFeedbackDialog = useCallback(() => {
    setFeedbackDialog((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleSaveFeedback = useCallback(async () => {
    const { requirementId, currentFeedback, actualQuestionId, clauseNo } = feedbackDialog;
    
    if (!requirementId) return;

    try {
      // Update local state first
      handleRemarkChange(requirementId, currentFeedback);

      // Use the stored actualQuestionId and clauseNo from dialog state
      const qid = actualQuestionId || requirementId;
      const finalClauseNo = clauseNo || "N/A";
      
      console.log('💾 Saving feedback with:', { 
        qid, 
        clauseNo: finalClauseNo, 
        requirementId,
        actualQuestionId
      });

      // Get selected NC items for this requirement
      const selectedNcItems = ncSelections.get(requirementId) || new Set();
      const ncItemsArray = Array.from(selectedNcItems);
      const ncString = ncItemsArray.length > 0 ? ncItemsArray.join(',') : '';

      const payload = {
        qid: String(qid),
        appid: String(appId),
        assessmentid: String(assessmentId),
        assessorid: String(assessorId),
        orgid: String(orgId),
        clauseno: String(finalClauseNo),
        nC_doc_id: '',
        remarks: currentFeedback,
      };

      console.log('📤 Feedback API payload:', payload);

      // Validate mandatory fields
      const requiredFields = ['qid', 'appid', 'assessmentid', 'assessorid', 'orgid', 'clauseno'];
      for (const field of requiredFields) {
        if (!payload[field] || payload[field] === 'undefined' || payload[field] === 'null') {
          console.warn(`Mandatory field missing: ${field}`);
          showSnackbar(`Required field missing: ${field}`, "error");
          return;
        }
      }

      const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Savechecklist`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      
      if (result.isstatus) {
        const wasAlreadySubmitted = submittedItems.has(requirementId);
        showSnackbar(
          wasAlreadySubmitted 
            ? "Feedback updated successfully!" 
            : "Feedback saved successfully!", 
          "success"
        );
        setSubmittedItems((prev) => new Set([...prev, requirementId]));
        handleCloseFeedbackDialog();
      } else {
        console.error('❌ Failed to save feedback:', result.message || result);
        showSnackbar(result.message || "Failed to save feedback", "error");
      }
    } catch (error) {
      console.error('❌ Error saving feedback:', error);
      showSnackbar("Error saving feedback", "error");
    }
  }, [feedbackDialog, handleRemarkChange, handleCloseFeedbackDialog, showSnackbar, requirements, ncSelections, appId, assessmentId, assessorId, orgId, submittedItems]);

  const handleFeedbackTextChange = useCallback((value) => {
    setFeedbackDialog((prev) => ({
      ...prev,
      currentFeedback: value,
    }));
  }, []);

  // PDF Viewer Handlers
  const handleOpenPdfViewer = useCallback((url, title = "Document Viewer") => {
    setPdfViewer({
      open: true,
      url: url,
      title: title,
    });
  }, []);

  const handleClosePdfViewer = useCallback(() => {
    setPdfViewer((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleNcChange = useCallback(async (itemId, ncOptionId, checked) => {
    console.log(`🔄 handleNcChange called:`, { itemId, ncOptionId, checked });

    // Update local state first
    setNcSelections((prev) => {
      const newMap = new Map(prev);
      const currentSelections = newMap.get(itemId) || new Set();

      console.log(
        `📋 Current selections for ${itemId}:`,
        Array.from(currentSelections)
      );

      if (checked) {
        currentSelections.add(ncOptionId);
        console.log(`✅ Added NC option ID: ${ncOptionId}`);
      } else {
        currentSelections.delete(ncOptionId);
        console.log(`❌ Removed NC option ID: ${ncOptionId}`);
      }

      newMap.set(itemId, currentSelections);
      console.log(
        `💾 Updated selections for ${itemId}:`,
        Array.from(currentSelections)
      );
      console.log(`📊 Total ncSelections size:`, newMap.size);

      return newMap;
    });

    // Call API when checkbox is checked/unchecked
    try {
      if (checked) {
        // Call /Savenc API when checkbox is CHECKED
        // Get clauseNo from requirements structure
        let clauseNo = null;
        let ncItemText = '';
        
        for (const section of requirements) {
          for (const subSection of section.requirements) {
            // Check if it's a question in the subsection
            if (subSection.questions) {
              for (let qIndex = 0; qIndex < subSection.questions.length; qIndex++) {
                const questionItemId = `${subSection.id}_q_${qIndex}`;
                if (questionItemId === itemId) {
                  const question = subSection.questions[qIndex];
                  clauseNo = question.ClauseNo || question.clauseNo || question.DisplayName || '';
                  break;
                }
              }
            }
            // Check if it's a requirement in the subsection
            for (const requirement of subSection.requirements) {
              if (requirement.id === itemId) {
                clauseNo = requirement.clause || requirement.ClauseNo || requirement.clauseNo || '';
                break;
              }
            }
          }
        }

        // Get the NC item text from the ncOptionId
        const ncOptions = getNcOptionsForRequirement(clauseNo || itemId);
        const ncOption = ncOptions.find(opt => opt.id === ncOptionId);
        //ncItemText = ncOption ? ncOption.text : '';
        ncItemText = ncOption ? (ncOption.text?.includes("<p>") ? ncOption.text.replace(/<\/?p>/g, '').trim() : ncOption.text || '') : '';

        // Get existing evidence data for this NC item
        const evidenceKey = `${ncItemText}_${clauseNo}`;
        const existingEvidence = evidenceData.get(evidenceKey);

        // Get remarks from the remarks Map (TextField value)
        const currentRemarks = remarks.get(itemId) || '';
        
        console.log(`📝 Looking for remarks with itemId: ${itemId}`);
        console.log(`📝 Found remarks value:`, currentRemarks);
        console.log(`📝 All remarks keys:`, Array.from(remarks.keys()));
        console.log(`📝 All remarks entries:`, Array.from(remarks.entries()));

        // Prepare payload for /Savenc API
        const payload = {
          ncid: String(ncOptionId),
          appid: String(appId || ''),
          assessmentid: String(assessmentId || ''),
          assessorid: String(assessorId || ''),
          orgid: String(orgId || ''),
          clauseno: String(clauseNo || ''),
          remarks: currentRemarks, // Use remarks from TextField
          document: existingEvidence?.imageUrl || '',
          description: ncItemText
        };

        console.log('📤 Calling /Savenc API with payload:', payload);

        const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Savenc`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (result.success || result.isstatus) {
          console.log('✅ NC saved successfully via /Savenc:', result.message || result);
          showSnackbar('NC added successfully!', 'success');
        } else {
          console.error('❌ Failed to save NC:', result.message || result);
          showSnackbar(result.message || 'Failed to save NC item', 'error');
        }
      } else {
        const deleteUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/DeleteNC?appid=${appId}&ncid=${ncOptionId}`;
        const deleteResponse = await axios.delete(deleteUrl)
        const deleteResult = deleteResponse.data;
        if (deleteResult.success || deleteResult.isstatus) {
          console.log('✅ NC deleted successfully via /DeleteNC:', deleteResult.message || deleteResult);
          showSnackbar('NC removed successfully!', 'success');
          
          // Clear evidence data for this NC item if it exists
          const ncOptions = getNcOptionsForRequirement(itemId);
          const ncOption = ncOptions.find(opt => opt.id === ncOptionId);
          if (ncOption) {
            let clauseNo = null;
            for (const section of requirements) {
              for (const subSection of section.requirements) {
                if (subSection.questions) {
                  for (let qIndex = 0; qIndex < subSection.questions.length; qIndex++) {
                    const questionItemId = `${subSection.id}_q_${qIndex}`;
                    if (questionItemId === itemId) {
                      const question = subSection.questions[qIndex];
                      clauseNo = question.ClauseNo || question.clauseNo || question.DisplayName || '';
                      break;
                    }
                  }
                }
                for (const requirement of subSection.requirements) {
                  if (requirement.id === itemId) {
                    clauseNo = requirement.clause || requirement.ClauseNo || requirement.clauseNo || '';
                    break;
                  }
                }
              }
            }
            const evidenceKey = `${ncOption.text}_${clauseNo}`;
            setEvidenceData(prev => {
              const newMap = new Map(prev);
              newMap.delete(evidenceKey);
              return newMap;
            });
          }
        } else {
          console.error('❌ Failed to delete NC:', deleteResult.message || deleteResult);
          showSnackbar(deleteResult.message || 'Failed to delete NC item', 'error');
        }
      }
    } catch (error) {
      console.error('❌ Error calling API:', error);
      showSnackbar(checked ? 'Error saving NC item' : 'Error deleting NC item', 'error');
    }

    // Also call handleSubmit to save the checklist
    console.log(`NC checkbox updated for ${itemId}, ID: ${ncOptionId}, checked: ${checked}`);
    //handleSubmit(itemId);
  }, [requirements, appId, assessmentId, assessorId, orgId, evidenceData, remarks, getNcOptionsForRequirement, showSnackbar]);

  const getSubmitTooltip = (itemId) => {
    if (isSubmitted(itemId)) {
      return "Click to update this submission";
    }
    const selectedNcItems = ncSelections.get(itemId) || new Set();
    const hasRemarks = remarks.get(itemId)?.trim() || "";

    if (selectedNcItems.size > 0 && hasRemarks) {
      return "Submit with non-conformance and remarks";
    } else if (selectedNcItems.size > 0) {
      return "Submit with non-conformance";
    } else if (hasRemarks) {
      return "Submit with remarks";
    } else {
      return "Submit as compliant";
    }
  };

  const getSubmitButtonText = (itemId) => {
    if (isSubmitted(itemId)) {
      return "Update";
    }
    const selectedNcItems = ncSelections.get(itemId) || new Set();
    const hasNcSelected = selectedNcItems.size > 0;
    return hasNcSelected ? "Save" : "Save";
  };

  const handleAccordionChange = useCallback(
    (accordionId, type = "subsection", data = null) =>
      (event, isExpanded) => {
        setExpandedAccordions((prev) => {
          const newSet = new Set(prev);
          if (isExpanded) {
            newSet.add(accordionId);

            // Trigger lazy loading based on type
            if (type === "category" && data) {
              // Load subcategories for this category
              handleCategoryExpand(data);
            } else if (type === "subcategory" && data) {
              // Load questions for this subcategory
              handleSubcategoryExpand(data);
              
              // Also trigger checkbox binding after subcategory expansion
              setTimeout(() => {
                console.log('🔗 Triggering checkbox binding after subcategory expansion...');
                fetchSavedChecklist();
              }, 500);
            }

            setTimeout(() => {
              const element = event.currentTarget?.closest('.MuiAccordion-root');
              if (element) {
                element.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                  inline: 'nearest'
                });
                // Add a small offset to account for fixed headers
                window.scrollBy(0, -80);
              }
              
              // Also trigger checkbox binding after expansion for any visible questions
              console.log('🔗 Triggering checkbox binding after accordion expansion...');
              fetchSavedChecklist();
            }, 300);
          } else {
            newSet.delete(accordionId);
          }
          return newSet;
        });
      },
    [handleCategoryExpand, handleSubcategoryExpand, fetchSavedChecklist]
  );

  const isSubmitted = (itemId) => submittedItems.has(itemId);

  return (
    <>
      <PageTitle title="NABL | Checklist" />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Loading State for Dynamic Requirements */}
        {requirementsLoading && (
          <>
            {/* Loading Skeleton for Better UX */}
            <Box sx={{ mb: 4 }}>
              {[1, 2, 3].map((item) => (
                <Card key={item} elevation={2} sx={{ mb: 2, p: 2 }}>
                  <Skeleton variant="text" width="30%" height={32} />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={120}
                    sx={{ mt: 1 }}
                  />
                  <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                    <Skeleton variant="rectangular" width={100} height={36} />
                    <Skeleton variant="rectangular" width={100} height={36} />
                    <Skeleton variant="rectangular" width={80} height={36} />
                  </Box>
                </Card>
              ))}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading requirements...</Typography>
            </Box>
          </>
        )}

        {/* Error State for Dynamic Requirements */}
        {requirementsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {requirementsError}
            <Button
              onClick={buildDynamicRequirements}
              sx={{ ml: 2 }}
              size="small"
              variant="outlined"
            >
              Retry
            </Button>
          </Alert>
        )}

        {/* Show loading message for requirements */}
        {(!daqmsCategories.length || !daqmsSubCategories.length) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Loading {labType} requirements data...
          </Alert>
        )}

        {/* Show error if no requirements available */}
        {daqmsCategories.length === 0 && daqmsSubCategories.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No {labType} requirements data available. Please check your database
            connection.
          </Alert>
        )}

        {/* Search and Pagination Controls */}
        {!requirementsLoading && requirements && requirements.length > 0 && (
          <Card elevation={2} sx={{ mb: 3, p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search requirements..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <FilterList sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  onClick={handleApplicationChecklist}
                  endIcon={<NavigateNext />}
                >
                  Application Finding
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Typography variant="body2" sx={{ mr: 2 }}>
                    Showing {Math.min(visibleRequirements, requirements.length)}{" "}
                    of {requirements.length} sections
                  </Typography>
                  {visibleRequirements < requirements.length && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setLoadingMore(true);
                        setTimeout(() => {
                          setVisibleRequirements((prev) => prev + 5);
                          setLoadingMore(false);
                        }, 300);
                      }}
                      disabled={loadingMore}
                      startIcon={
                        loadingMore ? <CircularProgress size={16} /> : null
                      }
                    >
                      {loadingMore ? "Loading..." : "Load More"}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>
        )}

        {/* Enhanced Accordions with Dynamic Data */}
        {!requirementsLoading &&
          !requirementsError &&
          requirements &&
          requirements.length > 0 &&
          requirements
            .filter((section) => {
              if (!debouncedSearchFilter) return true;
              return (
                section.title
                  ?.toLowerCase()
                  .includes(debouncedSearchFilter.toLowerCase()) ||
                section.clause?.includes(debouncedSearchFilter) ||
                section.requirements?.some((req) =>
                  req.title
                    ?.toLowerCase()
                    .includes(debouncedSearchFilter.toLowerCase()),
                )
              );
            })
            .slice(0, visibleRequirements)
            .map((mainSection, index) => (
              <Card
                key={mainSection.id}
                elevation={4}
                sx={{
                  mb: 3,
                  borderRadius: 4,
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    elevation: 6,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Accordion
                  expanded={expandedAccordions.has(mainSection.id)}
                  onChange={handleAccordionChange(
                    mainSection.id,
                    "category",
                    mainSection,
                  )}
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    background:
                      "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMore sx={{ color: "#1976d2", fontSize: "2rem" }} />
                    }
                    sx={{
                      background:
                        "linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)",
                      minHeight: 80,
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #bbdefb 0%, #e1bee7 100%)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Badge
                        badgeContent={mainSection.requirements.length}
                        color="primary"
                        sx={{ mr: 2 }}
                      >
                        <AssignmentTurnedIn sx={{ color: "#1976d2", mr: 2 }} />
                      </Badge>
                      <Box>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color="#1976d2"
                        >
                          {mainSection.clause} {mainSection.title}
                        </Typography>
                        {/* <Typography variant="caption" color="#666">
                          Field: {mainSection.field} | Standard:{" "}
                          {mainSection.standard}
                        </Typography> */}
                        <Typography variant="caption" color="#666">
                          Field: {labType} | Standard: {mainSection.standard}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {/* Show loading state while fetching subcategories */}
                    {loadingSubcategories &&
                      expandedAccordions.has(mainSection.id) && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            p: 4,
                          }}
                        >
                          <CircularProgress />
                          <Typography sx={{ ml: 2 }}>
                            Loading subcategories...
                          </Typography>
                        </Box>
                      )}

                    {/* Show subcategories if loaded */}
                    {!loadingSubcategories &&
                      mainSection.requirements.map((subSection, subIndex) => (
                        <Card
                          key={subSection.id}
                          elevation={2}
                          sx={{
                            m: 2,
                            borderRadius: 3,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              elevation: 4,
                            },
                          }}
                        >
                          <Accordion
                            expanded={expandedAccordions.has(subSection.id)}
                            onChange={handleAccordionChange(
                              subSection.id,
                              "subcategory",
                              subSection,
                            )}
                            sx={{
                              boxShadow: "none",
                              "&:before": { display: "none" },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <ExpandMore
                                  sx={{
                                    color: expandedAccordions.has(subSection.id)
                                      ? "#fff"
                                      : "#666",
                                    transition: "color 0.3s ease",
                                  }}
                                />
                              }
                              sx={{
                                background: expandedAccordions.has(
                                  subSection.id,
                                )
                                  ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                                  : "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
                                borderRadius: "12px 12px 0 0",
                                minHeight: 64,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: expandedAccordions.has(
                                    subSection.id,
                                  )
                                    ? "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)"
                                    : "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                                },
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Lightbulb
                                  sx={{
                                    color: expandedAccordions.has(subSection.id)
                                      ? "#fff"
                                      : "#ff9800",
                                    mr: 2,
                                    transition: "color 0.3s ease",
                                  }}
                                />
                                <Typography
                                  variant="body1"
                                  fontWeight="medium"
                                  color={
                                    expandedAccordions.has(subSection.id)
                                      ? "#fff"
                                      : "#333"
                                  }
                                  sx={{
                                    fontSize: "1.1rem",
                                    transition: "color 0.3s ease",
                                  }}
                                >
                                  {subSection.clause} {subSection.title}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              {/* Show loading state while fetching questions */}
                              {loadingQuestions &&
                                expandedAccordions.has(subSection.id) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      p: 4,
                                    }}
                                  >
                                    <CircularProgress size={24} />
                                    <Typography sx={{ ml: 2 }}>
                                      Loading questions...
                                    </Typography>
                                  </Box>
                                )}

                              {/* Show questions table if loaded */}
                              {!loadingQuestions && (
                                <TableContainer
                                  sx={{
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 2,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Table
                                    sx={{
                                      borderCollapse: "separate",
                                      borderSpacing: 0,
                                      "& .MuiTableCell-root": {
                                        borderRight: "1px solid #e0e0e0",
                                        borderBottom: "1px solid #e0e0e0",
                                        "&:last-child": {
                                          borderRight: "none",
                                        },
                                      },
                                    }}
                                  >
                                    {/* Table Header for Questions */}
                                    {subSection.questions &&
                                      subSection.questions.filter(
                                        (question) => {
                                          const questionClause =
                                            question.ClauseNo ||
                                            question.clauseNo ||
                                            question.clause;
                                          const subsectionClause =
                                            subSection.clause;
                                          if (
                                            !questionClause ||
                                            !subsectionClause
                                          )
                                            return true;
                                          return (
                                            questionClause.toString() ===
                                            subsectionClause.toString()
                                          );
                                        },
                                      ).length > 0 && (
                                        <TableHead>
                                          <TableRow
                                            sx={{
                                              backgroundColor: "#f5f5f5",
                                              "& .MuiTableCell-head": {
                                                fontWeight: "bold",
                                                fontSize: "0.8rem",
                                                color: "#1976d2",
                                                py: 1,
                                              },
                                            }}
                                          >
                                            <TableCell>Clause No.</TableCell>
                                            <TableCell>
                                              Standard Statement
                                            </TableCell>
                                            <TableCell>
                                              Compliance Requirement
                                            </TableCell>
                                            <TableCell>
                                              Non-Compliance
                                            </TableCell>
                                            <TableCell>
                                              <b>
                                                Remark/Observation/Suggestion on
                                                checklist content for NABL
                                              </b>
                                              <br />
                                              {/* <span
                                                style={{
                                                  fontSize: "12px",
                                                  fontStyle: "italic",
                                                  color: "#666",
                                                }}
                                              >
                                                (This will not be considered as
                                                part of the report)
                                              </span> */}
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                      )}
                                    <TableBody>
                                      {/* Generate rows for each question in the subsection */}
                                      {subSection.questions &&
                                        (() => {
                                          const sectionId = `${mainSection.id}_${subSection.id}`;
                                          const filteredAndSortedQuestions =
                                            subSection.questions.filter(
                                              (question) => {
                                                // Filter questions to match current subsection
                                                const questionClause =
                                                  question.ClauseNo ||
                                                  question.clauseNo ||
                                                  question.clause;
                                                const subsectionClause =
                                                  subSection.clause;
                                                if (
                                                  !questionClause ||
                                                  !subsectionClause
                                                )
                                                  return true;
                                                return (
                                                  questionClause.toString() ===
                                                  subsectionClause.toString()
                                                );
                                              },
                                            );

                                          // Initialize question limit for this section
                                          const currentLimit =
                                            questionsPerSection.get(
                                              sectionId,
                                            ) || 3;
                                          const totalQuestions =
                                            filteredAndSortedQuestions.length;

                                          // Initialize if not already done
                                          if (
                                            !questionsPerSection.has(
                                              sectionId,
                                            ) &&
                                            totalQuestions > 0
                                          ) {
                                            initializeQuestionLimit(
                                              sectionId,
                                              totalQuestions,
                                            );
                                          }

                                          const visibleQuestions =
                                            filteredAndSortedQuestions.slice(
                                              0,
                                              currentLimit,
                                            );
                                          const hasMoreQuestions =
                                            totalQuestions > currentLimit;

                                          return (
                                            <>
                                              {visibleQuestions.map(
                                                (question, qIndex) => (
                                                  <TableRow
                                                    key={`${subSection.id}_q_${qIndex}`}
                                                    sx={{
                                                      "&:nth-of-type(odd)": {
                                                        backgroundColor:
                                                          "#f8f9fa",
                                                      },
                                                      "&:nth-of-type(even)": {
                                                        backgroundColor:
                                                          "#ffffff",
                                                      },
                                                      "&:hover": {
                                                        backgroundColor:
                                                          "rgba(33, 150, 243, 0.08)",
                                                      },
                                                      "&:last-child .MuiTableCell-root":
                                                        {
                                                          borderBottom: "none",
                                                        },
                                                      transition:
                                                        "all 0.2s ease",
                                                    }}
                                                  >
                                                    <TableCell
                                                      sx={{
                                                        minWidth: 100,
                                                        fontWeight: "bold",
                                                        color: "#000",
                                                        fontSize: "1.1rem",
                                                        p: 2,
                                                      }}
                                                    >
                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          flexDirection:
                                                            "column",
                                                          alignItems: "center",
                                                          gap: 1,
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body1"
                                                          sx={{
                                                            fontWeight: "bold",
                                                            color: "#000",
                                                          }}
                                                        >
                                                          {question.DisplayName}
                                                        </Typography>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell
                                                      dangerouslySetInnerHTML={{
                                                        __html:
                                                          question.Question ||
                                                          "N/A",
                                                      }}
                                                      sx={{
                                                        minWidth: 200,
                                                        p: 2,
                                                        verticalAlign: "top",
                                                      }}
                                                    >
                                                      {/* <Box sx={{ mb: 1 }}>
                                            
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: "0.9rem",
                                                  lineHeight: 1.6,
                                                  color: "#000",
                                                  p: 1.5,
                                                }}
                                              >
                                                {question.Question}
                                              </Typography>
                                            </Box> */}
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{
                                                        p: 2,
                                                        verticalAlign: "top",
                                                      }}
                                                    >
                                                      {(() => {
                                                        const questionClause =
                                                          question.DisplayName ||
                                                          question.ClauseNo ||
                                                          question.clauseNo;
                                                        const implementationText =
                                                          getImplementationForClause(
                                                            questionClause,
                                                          );
                                                        const availableTemplates =
                                                          getTemplatesForClause(
                                                            questionClause,
                                                          );
                                                        // const evidenceImages =
                                                        //   getEvidenceForClause(
                                                        //     questionClause
                                                        //   );

                                                        return (
                                                          <Box>
                                                            {/* Evidence Images Section */}
                                                            {/* {evidenceImages.length >
                                                            0 && (
                                                            <Box sx={{ mb: 2 }}>
                                                              <Typography
                                                                variant="caption"
                                                                sx={{
                                                                  fontWeight:
                                                                    "bold",
                                                                  color:
                                                                    "#1976d2",
                                                                  display:
                                                                    "block",
                                                                  mb: 1,
                                                                }}
                                                              >
                                                                Evidence (
                                                                {
                                                                  evidenceImages.length
                                                                }
                                                                ):
                                                              </Typography>
                                                              <Box
                                                                sx={{
                                                                  display:
                                                                    "flex",
                                                                  flexWrap:
                                                                    "wrap",
                                                                  gap: 1,
                                                                }}
                                                              >
                                                                {evidenceImages.map(
                                                                  (
                                                                    evidence,
                                                                    idx
                                                                  ) => (
                                                                    <Box
                                                                      key={idx}
                                                                      sx={{
                                                                        position:
                                                                          "relative",
                                                                        width: 80,
                                                                        height: 80,
                                                                        borderRadius: 1,
                                                                        overflow:
                                                                          "hidden",
                                                                        border:
                                                                          "2px solid #e0e0e0",
                                                                        cursor:
                                                                          "pointer",
                                                                        transition:
                                                                          "all 0.2s",
                                                                        "&:hover":
                                                                          {
                                                                            transform:
                                                                              "scale(1.05)",
                                                                            border:
                                                                              "2px solid #1976d2",
                                                                            boxShadow: 2,
                                                                          },
                                                                      }}
                                                                      onClick={() =>
                                                                        window.open(
                                                                          evidence.signedUrl,
                                                                          "_blank"
                                                                        )
                                                                      }
                                                                    >
                                                                      <img
                                                                        src={
                                                                          evidence.signedUrl
                                                                        }
                                                                        alt={`Evidence ${
                                                                          idx +
                                                                          1
                                                                        }`}
                                                                        style={{
                                                                          width:
                                                                            "100%",
                                                                          height:
                                                                            "100%",
                                                                          objectFit:
                                                                            "cover",
                                                                        }}
                                                                        onError={(
                                                                          e
                                                                        ) => {
                                                                          e.target.src =
                                                                            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23f0f0f0" width="80" height="80"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="sans-serif" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E';
                                                                        }}
                                                                      />
                                                                      <Tooltip
                                                                        title={
                                                                          evidence.fileName
                                                                        }
                                                                      >
                                                                        <Box
                                                                          sx={{
                                                                            position:
                                                                              "absolute",
                                                                            bottom: 0,
                                                                            left: 0,
                                                                            right: 0,
                                                                            bgcolor:
                                                                              "rgba(0, 0, 0, 0.6)",
                                                                            color:
                                                                              "white",
                                                                            fontSize:
                                                                              "0.65rem",
                                                                            p: 0.5,
                                                                            textAlign:
                                                                              "center",
                                                                            whiteSpace:
                                                                              "nowrap",
                                                                            overflow:
                                                                              "hidden",
                                                                            textOverflow:
                                                                              "ellipsis",
                                                                          }}
                                                                        >
                                                                          {idx +
                                                                            1}
                                                                        </Box>
                                                                      </Tooltip>
                                                                    </Box>
                                                                  )
                                                                )}
                                                              </Box>
                                                            </Box>
                                                          )} */}

                                                            {/* Implementation Text */}
                                                            {implementationText ? (
                                                              <Box
                                                                sx={{
                                                                  p: 2,
                                                                  backgroundColor:
                                                                    "rgba(33, 150, 243, 0.05)",
                                                                  borderRadius: 2,
                                                                  borderLeft:
                                                                    "4px solid #1976d2",
                                                                  mb:
                                                                    availableTemplates.length >
                                                                    0
                                                                      ? 2
                                                                      : 0,
                                                                  color:
                                                                    "#1976d2",
                                                                  "& *": {
                                                                    color:
                                                                      "#1976d2 !important",
                                                                  },
                                                                }}
                                                                dangerouslySetInnerHTML={{
                                                                  __html:
                                                                    implementationText,
                                                                }}
                                                              ></Box>
                                                            ) : (
                                                              ""
                                                            )}

                                                            {/* Templates Section */}
                                                            {availableTemplates.length >
                                                              0 && (
                                                              <Box>
                                                                <Chip
                                                                  icon={
                                                                    <Lightbulb />
                                                                  }
                                                                  label={`Document for Compliance (${availableTemplates.length})`}
                                                                  color="error"
                                                                  variant="outlined"
                                                                  size="small"
                                                                  sx={{ mb: 1 }}
                                                                />

                                                                <Box>
                                                                  {availableTemplates.map(
                                                                    (
                                                                      template,
                                                                      index,
                                                                    ) => (
                                                                      <Box
                                                                        key={
                                                                          index
                                                                        }
                                                                        sx={{
                                                                          mt: 1,
                                                                          p: 1.5,
                                                                          backgroundColor:
                                                                            "rgba(255, 72, 0, 0.14)",
                                                                          borderRadius: 1,
                                                                          border:
                                                                            "1px solid rgba(255, 72, 0, 0.2)",
                                                                        }}
                                                                      >
                                                                        <Box
                                                                          sx={{
                                                                            display:
                                                                              "flex",
                                                                            justifyContent:
                                                                              "space-between",
                                                                            alignItems:
                                                                              "flex-start",
                                                                          }}
                                                                        >
                                                                          <Box
                                                                            sx={{
                                                                              flex: 1,
                                                                            }}
                                                                          >
                                                                            <Box
                                                                              className="listform-html-content"
                                                                              sx={{
                                                                                color:
                                                                                  "#5c1104ff",
                                                                                fontWeight:
                                                                                  "medium",
                                                                                fontSize:
                                                                                  "0.8rem",
                                                                                mb: 1,
                                                                              }}
                                                                              dangerouslySetInnerHTML={{
                                                                                __html:
                                                                                  template.Name ||
                                                                                  template.TemplateName ||
                                                                                  template.templateName ||
                                                                                  `Template ${
                                                                                    index +
                                                                                    1
                                                                                  }`,
                                                                              }}
                                                                            />
                                                                            {template.Description && (
                                                                              <Box
                                                                                className="listform-html-content"
                                                                                dangerouslySetInnerHTML={{
                                                                                  __html:
                                                                                    template.Description,
                                                                                }}
                                                                              />
                                                                            )}
                                                                          </Box>

                                                                          {/* View button if PDF file is available */}
                                                                          {template.Listfile && (
                                                                            <Button
                                                                              variant="outlined"
                                                                              size="small"
                                                                              color="primary"
                                                                              onClick={() => {
                                                                                handleOpenPdfViewer(
                                                                                  template.Listfile,
                                                                                  template.FormatName ||
                                                                                    "Document",
                                                                                );
                                                                              }}
                                                                              sx={{
                                                                                ml: 1,
                                                                                minWidth:
                                                                                  "auto",
                                                                                px: 1,
                                                                                fontSize:
                                                                                  "0.7rem",
                                                                                textTransform:
                                                                                  "none",
                                                                              }}
                                                                            >
                                                                              View
                                                                              PDF
                                                                            </Button>
                                                                          )}
                                                                        </Box>

                                                                        {/* Show clause info for debugging */}
                                                                        <Typography
                                                                          variant="caption"
                                                                          sx={{
                                                                            display:
                                                                              "block",
                                                                            mt: 0.5,
                                                                            color:
                                                                              "#999",
                                                                            fontSize:
                                                                              "0.7rem",
                                                                          }}
                                                                        >
                                                                          Clause:{" "}
                                                                          {
                                                                            template.clauseno
                                                                          }
                                                                        </Typography>
                                                                      </Box>
                                                                    ),
                                                                  )}
                                                                </Box>
                                                              </Box>
                                                            )}
                                                          </Box>
                                                        );
                                                      })()}
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{
                                                        minWidth: 200,
                                                        p: 2,
                                                      }}
                                                    >
                                                      {getNcOptionsForRequirement(
                                                        question,
                                                      ).length > 0 ? (
                                                        <>
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              fontWeight:
                                                                "bold",
                                                              color: "#1976d2",
                                                              mb: 1,
                                                              display: "block",
                                                            }}
                                                          >
                                                            Non-Compliance
                                                            Items:
                                                          </Typography>
                                                          <FormGroup>
                                                            {getNcOptionsForRequirement(
                                                              question,
                                                            ).map(
                                                              (
                                                                ncOption,
                                                                index,
                                                              ) => {
                                                                const itemId = `${subSection.id}_q_${qIndex}`;
                                                                const questionClause =
                                                                  question.DisplayName ||
                                                                  question.ClauseNo ||
                                                                  question.clauseNo;
                                                                const evidenceKey = `${ncOption.id}_${questionClause}`;
                                                                const selectedNcSet =
                                                                  ncSelections.get(
                                                                    itemId,
                                                                  ) ||
                                                                  new Set();
                                                                const isChecked =
                                                                  selectedNcSet.has(
                                                                    ncOption.id,
                                                                  );

                                                                // Debug logging
                                                                if (
                                                                  index === 0
                                                                ) {
                                                                  console.log(
                                                                    `🔍 Checkbox check for ${questionClause}:`,
                                                                    {
                                                                      itemId,
                                                                      ncOptionId:
                                                                        ncOption.id,
                                                                      ncOptionIdType:
                                                                        typeof ncOption.id,
                                                                      selectedNcSet:
                                                                        Array.from(
                                                                          selectedNcSet,
                                                                        ),
                                                                      selectedNcSetTypes:
                                                                        Array.from(
                                                                          selectedNcSet,
                                                                        ).map(
                                                                          (
                                                                            id,
                                                                          ) =>
                                                                            typeof id,
                                                                        ),
                                                                      isChecked,
                                                                    },
                                                                  );
                                                                }

                                                                const hasEvidenceData =
                                                                  hasEvidence(
                                                                    ncOption.text,
                                                                    questionClause,
                                                                  );

                                                                return (
                                                                  <Box
                                                                    key={index}
                                                                    sx={{
                                                                      display:
                                                                        "flex",
                                                                      alignItems:
                                                                        "center",
                                                                      mb: 1,
                                                                    }}
                                                                  >
                                                                    <FormControlLabel
                                                                      control={
                                                                        <Checkbox
                                                                          checked={
                                                                            isChecked
                                                                          }
                                                                          onChange={(
                                                                            e,
                                                                          ) =>
                                                                            handleNcChange(
                                                                              itemId,
                                                                              ncOption.id,
                                                                              e
                                                                                .target
                                                                                .checked,
                                                                            )
                                                                          }
                                                                          disabled={
                                                                            isChecklistCompleted
                                                                          }
                                                                          size="small"
                                                                          sx={{
                                                                            color:
                                                                              "#f44336",
                                                                            "&.Mui-checked":
                                                                              {
                                                                                color:
                                                                                  "#f44336",
                                                                              },
                                                                          }}
                                                                        />
                                                                      }
                                                                      label={
                                                                        // <Typography variant="caption">
                                                                        //   {
                                                                        //     ncOption.text
                                                                        //   }
                                                                        // </Typography>
                                                                        <Typography
                                                                          variant="caption"
                                                                          className="listform-html-content"
                                                                          dangerouslySetInnerHTML={{
                                                                            __html:
                                                                              ncOption.text,
                                                                          }}
                                                                        />
                                                                      }
                                                                      sx={{
                                                                        flex: 1,
                                                                      }}
                                                                    />
                                                                    {isChecked && (
                                                                      <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        color={
                                                                          hasEvidenceData
                                                                            ? "success"
                                                                            : "error"
                                                                        }
                                                                        onClick={() =>
                                                                          handleEvidenceModalOpen(
                                                                            ncOption.text,
                                                                            ncOption.id,
                                                                            questionClause,
                                                                          )
                                                                        }
                                                                        startIcon={
                                                                          hasEvidenceData ? (
                                                                            <CheckCircle />
                                                                          ) : (
                                                                            <CloudUpload />
                                                                          )
                                                                        }
                                                                        sx={{
                                                                          ml: 1,
                                                                          minWidth:
                                                                            "auto",
                                                                          px: 1.5,
                                                                          py: 0.5,
                                                                          fontSize:
                                                                            "0.7rem",
                                                                          textTransform:
                                                                            "none",
                                                                          fontWeight:
                                                                            "bold",
                                                                          borderRadius: 2,
                                                                          boxShadow: 2,
                                                                          "&:hover":
                                                                            {
                                                                              boxShadow: 4,
                                                                              transform:
                                                                                "translateY(-1px)",
                                                                            },
                                                                          transition:
                                                                            "all 0.2s ease",
                                                                        }}
                                                                      >
                                                                        {hasEvidenceData
                                                                          ? "View Details"
                                                                          : "Upload Evidence"}
                                                                      </Button>
                                                                    )}
                                                                  </Box>
                                                                );
                                                              },
                                                            )}
                                                          </FormGroup>
                                                        </>
                                                      ) : (
                                                        // <Typography
                                                        //   variant="caption"
                                                        //   sx={{
                                                        //     color: "#666",
                                                        //     fontStyle: "italic",
                                                        //   }}
                                                        // >
                                                        //   No non-compliance options available
                                                        // </Typography>
                                                        ""
                                                      )}
                                                    </TableCell>

                                                    <TableCell
                                                      sx={{
                                                        minWidth: 200,
                                                        p: 2,
                                                      }}
                                                    >
                                                      {hasNCChecklistForClause(
                                                        question.DisplayName,
                                                      ) && (
                                                        <Button
                                                          size="small"
                                                          color="warning"
                                                          variant="outlined"
                                                          onClick={() =>
                                                            handleOpenNCChecklistDialog(
                                                              question.DisplayName,
                                                            )
                                                          }
                                                          sx={{
                                                            padding: 0.5,
                                                            mb: 2,
                                                            "&:hover": {
                                                              backgroundColor:
                                                                "rgba(255, 152, 0, 0.1)",
                                                            },
                                                          }}
                                                        >
                                                          <Info fontSize="small" />
                                                          View Application
                                                          Finding
                                                        </Button>
                                                      )}
                                                      {(() => {
                                                        const itemId = `${subSection.id}_q_${qIndex}`;
                                                        const question =
                                                          visibleQuestions[
                                                            qIndex
                                                          ];
                                                        const clauseNo =
                                                          question.DisplayName ||
                                                          question.ClauseNo ||
                                                          question.clauseNo;
                                                        const existingRemark =
                                                          getExistingRemarkForClause(
                                                            clauseNo,
                                                          );
                                                        const hasExisting =
                                                          hasExistingRemark(
                                                            clauseNo,
                                                          );
                                                        const ncOptions =
                                                          getNcOptionsForRequirement(
                                                            question,
                                                          );
                                                        const hasNoNcOptions =
                                                          ncOptions.length ===
                                                          0;

                                                        // Don't show remarks field if no NC options are available
                                                        if (hasNoNcOptions) {
                                                          return null;
                                                        }

                                                        if (
                                                          hasExisting &&
                                                          existingRemark
                                                        ) {
                                                          return (
                                                            <Alert
                                                              severity="info"
                                                              sx={{
                                                                backgroundColor:
                                                                  "#e3f2fd",
                                                                border:
                                                                  "1px solid #2196f3",
                                                              }}
                                                            >
                                                              <Typography
                                                                variant="body2"
                                                                sx={{
                                                                  fontWeight:
                                                                    "medium",
                                                                }}
                                                              >
                                                                Existing Remark:
                                                              </Typography>
                                                              <Typography
                                                                variant="body2"
                                                                sx={{ mt: 0.5 }}
                                                              >
                                                                {
                                                                  existingRemark.Remark
                                                                }
                                                              </Typography>
                                                              <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                  mt: 1,
                                                                  display:
                                                                    "block",
                                                                }}
                                                              >
                                                                Saved on:{" "}
                                                                {new Date(
                                                                  existingRemark.InsertDate,
                                                                ).toLocaleDateString()}
                                                              </Typography>
                                                            </Alert>
                                                          );
                                                        } else {
                                                          const remarkValue =
                                                            remarks.get(
                                                              itemId,
                                                            ) || "";
                                                          const questionObj = {
                                                            ...question,
                                                            id: itemId,
                                                          };

                                                          return (
                                                            <Box>
                                                              <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() =>
                                                                  handleOpenFeedbackDialog(
                                                                    questionObj,
                                                                  )
                                                                }
                                                                disabled={
                                                                  isChecklistCompleted
                                                                }
                                                                startIcon={
                                                                  <Lightbulb />
                                                                }
                                                                fullWidth
                                                                sx={{
                                                                  textTransform:
                                                                    "none",
                                                                  borderRadius: 1,
                                                                  py: 1,
                                                                  borderColor:
                                                                    remarkValue
                                                                      ? "#1976d2"
                                                                      : "#ccc",
                                                                  color:
                                                                    remarkValue
                                                                      ? "#1976d2"
                                                                      : "#666",
                                                                  fontWeight:
                                                                    remarkValue
                                                                      ? "bold"
                                                                      : "normal",
                                                                  "&:hover": {
                                                                    borderColor:
                                                                      "#1976d2",
                                                                    backgroundColor:
                                                                      "#f5f5f5",
                                                                  },
                                                                }}
                                                              >
                                                                {remarkValue
                                                                  ? isChecklistCompleted
                                                                    ? "View Remark/Observation/Suggestion"
                                                                    : "View/Edit Remark"
                                                                  : isChecklistCompleted
                                                                    ? "No Remark/Observation/Suggestion"
                                                                    : "Add Remark/Observation/Suggestion on content of checklist for NABL"}
                                                              </Button>
                                                              {remarkValue && (
                                                                <Typography
                                                                  variant="caption"
                                                                  color="text.secondary"
                                                                  sx={{
                                                                    mt: 0.5,
                                                                    display:
                                                                      "block",
                                                                    whiteSpace:
                                                                      "nowrap",
                                                                    overflow:
                                                                      "hidden",
                                                                    textOverflow:
                                                                      "ellipsis",
                                                                  }}
                                                                >
                                                                  {remarkValue.substring(
                                                                    0,
                                                                    50,
                                                                  )}
                                                                  {remarkValue.length >
                                                                  50
                                                                    ? "..."
                                                                    : ""}
                                                                </Typography>
                                                              )}
                                                            </Box>
                                                          );
                                                        }
                                                      })()}
                                                    </TableCell>
                                                  </TableRow>
                                                ),
                                              )}

                                              {/* If no questions, show the original requirements structure */}
                                              {(!subSection.questions ||
                                                subSection.questions.filter(
                                                  (question) => {
                                                    const questionClause =
                                                      question.ClauseNo ||
                                                      question.clauseNo ||
                                                      question.clause;
                                                    const subsectionClause =
                                                      subSection.clause;
                                                    if (
                                                      !questionClause ||
                                                      !subsectionClause
                                                    )
                                                      return true;
                                                    return (
                                                      questionClause.toString() ===
                                                      subsectionClause.toString()
                                                    );
                                                  },
                                                ).length === 0) &&
                                                subSection.requirements.map(
                                                  (requirement, reqIndex) => (
                                                    <TableRow
                                                      key={requirement.id}
                                                      sx={{
                                                        "&:nth-of-type(odd)": {
                                                          backgroundColor:
                                                            "#f8f9fa",
                                                        },
                                                        "&:nth-of-type(even)": {
                                                          backgroundColor:
                                                            "#ffffff",
                                                        },
                                                        "&:hover": {
                                                          backgroundColor:
                                                            "rgba(33, 150, 243, 0.08)",
                                                        },
                                                        "&:last-child .MuiTableCell-root":
                                                          {
                                                            borderBottom:
                                                              "none",
                                                          },
                                                        transition:
                                                          "all 0.2s ease",
                                                      }}
                                                    >
                                                      <TableCell
                                                        sx={{
                                                          minWidth: 100,
                                                          fontWeight: "bold",
                                                          color: "#1976d2",
                                                          fontSize: "1.1rem",
                                                          p: 2,
                                                        }}
                                                      >
                                                        {requirement.clause}
                                                        {requirement.subclause}
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{
                                                          minWidth: 200,
                                                          p: 2,
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          sx={{
                                                            mb: 2,
                                                            lineHeight: 1.6,
                                                          }}
                                                        >
                                                          {
                                                            requirement.requirement
                                                          }
                                                        </Typography>

                                                        {/* Display DAQMS Questions in a clean section */}
                                                        {subSection.questions &&
                                                          subSection.questions
                                                            .length > 0 &&
                                                          subSection.questions.filter(
                                                            (question) => {
                                                              // Only show questions that match the current subsection clause
                                                              const questionClause =
                                                                question.ClauseNo ||
                                                                question.clauseNo ||
                                                                question.clause;
                                                              const subsectionClause =
                                                                subSection.clause;

                                                              // If no clause info, show all questions for this subsection
                                                              if (
                                                                !questionClause ||
                                                                !subsectionClause
                                                              ) {
                                                                return true;
                                                              }

                                                              // Match clause numbers
                                                              return (
                                                                questionClause.toString() ===
                                                                subsectionClause.toString()
                                                              );
                                                            },
                                                          ).length > 0 && (
                                                            <Box sx={{ mt: 2 }}>
                                                              <Typography
                                                                variant="caption"
                                                                color="#1976d2"
                                                                sx={{
                                                                  fontWeight:
                                                                    "bold",
                                                                  mb: 1,
                                                                  display:
                                                                    "block",
                                                                }}
                                                              >
                                                                DAQMS Questions
                                                                (
                                                                {
                                                                  subSection.questions.filter(
                                                                    (
                                                                      question,
                                                                    ) => {
                                                                      const questionClause =
                                                                        question.ClauseNo ||
                                                                        question.clauseNo ||
                                                                        question.clause;
                                                                      const subsectionClause =
                                                                        subSection.clause;
                                                                      if (
                                                                        !questionClause ||
                                                                        !subsectionClause
                                                                      )
                                                                        return true;
                                                                      return (
                                                                        questionClause.toString() ===
                                                                        subsectionClause.toString()
                                                                      );
                                                                    },
                                                                  ).length
                                                                }
                                                                ):
                                                              </Typography>
                                                              {subSection.questions
                                                                .filter(
                                                                  (
                                                                    question,
                                                                  ) => {
                                                                    // Filter questions to match current subsection
                                                                    const questionClause =
                                                                      question.ClauseNo ||
                                                                      question.clauseNo ||
                                                                      question.clause;
                                                                    const subsectionClause =
                                                                      subSection.clause;
                                                                    if (
                                                                      !questionClause ||
                                                                      !subsectionClause
                                                                    )
                                                                      return true;
                                                                    return (
                                                                      questionClause.toString() ===
                                                                      subsectionClause.toString()
                                                                    );
                                                                  },
                                                                )
                                                                .map(
                                                                  (
                                                                    question,
                                                                    qIndex,
                                                                  ) => (
                                                                    <Box
                                                                      key={
                                                                        qIndex
                                                                      }
                                                                      sx={{
                                                                        mb: 1,
                                                                        p: 1.5,
                                                                        backgroundColor:
                                                                          "#f0f8ff",
                                                                        borderRadius: 1,
                                                                        border:
                                                                          "1px solid #e3f2fd",
                                                                      }}
                                                                    >
                                                                      <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                          fontSize:
                                                                            "0.9rem",
                                                                          mb: 0.5,
                                                                        }}
                                                                      >
                                                                        <strong>
                                                                          Q
                                                                          {qIndex +
                                                                            1}
                                                                          :
                                                                        </strong>{" "}
                                                                        {
                                                                          question.QuestionText
                                                                        }
                                                                      </Typography>
                                                                      {question.Options && (
                                                                        <Typography
                                                                          variant="caption"
                                                                          sx={{
                                                                            color:
                                                                              "text.secondary",
                                                                            fontStyle:
                                                                              "italic",
                                                                          }}
                                                                        >
                                                                          Options:{" "}
                                                                          {
                                                                            question.Options
                                                                          }
                                                                        </Typography>
                                                                      )}
                                                                    </Box>
                                                                  ),
                                                                )}
                                                            </Box>
                                                          )}
                                                      </TableCell>
                                                      <TableCell sx={{ p: 2 }}>
                                                        {requirement.implementation && (
                                                          <Box
                                                            sx={{
                                                              p: 2,
                                                              backgroundColor:
                                                                "rgba(33, 150, 243, 0.05)",
                                                              borderRadius: 2,
                                                              borderLeft:
                                                                "4px solid #1976d2",
                                                            }}
                                                          >
                                                            <Typography
                                                              variant="caption"
                                                              color="#1976d2"
                                                              sx={{
                                                                lineHeight: 1.5,
                                                                fontSize:
                                                                  "0.8rem",
                                                              }}
                                                            >
                                                              {
                                                                requirement.implementation
                                                              }
                                                            </Typography>
                                                          </Box>
                                                        )}
                                                        {(() => {
                                                          // Get dynamic templates from database
                                                          const requirementClause =
                                                            requirement.clause;
                                                          const availableTemplates =
                                                            getTemplatesForClause(
                                                              requirementClause,
                                                            );

                                                          // Show database templates if available, otherwise show static templates
                                                          const hasTemplates =
                                                            availableTemplates.length >
                                                              0 ||
                                                            requirement.templates;

                                                          if (hasTemplates) {
                                                            return (
                                                              <Box
                                                                sx={{ mt: 2 }}
                                                              >
                                                                <Chip
                                                                  icon={
                                                                    <Lightbulb />
                                                                  }
                                                                  label={`Templates Available (${
                                                                    availableTemplates.length ||
                                                                    1
                                                                  })`}
                                                                  color="success"
                                                                  variant="outlined"
                                                                  size="small"
                                                                />

                                                                {/* Dynamic templates from ListFormats table */}
                                                                {availableTemplates.length >
                                                                  0 && (
                                                                  <Box
                                                                    sx={{
                                                                      mt: 1,
                                                                    }}
                                                                  >
                                                                    {availableTemplates.map(
                                                                      (
                                                                        template,
                                                                        index,
                                                                      ) => (
                                                                        <Box
                                                                          key={
                                                                            index
                                                                          }
                                                                          sx={{
                                                                            mt: 1,
                                                                            p: 1.5,
                                                                            backgroundColor:
                                                                              "rgba(255, 152, 0, 0.05)",
                                                                            borderRadius: 1,
                                                                            border:
                                                                              "1px solid rgba(255, 152, 0, 0.2)",
                                                                          }}
                                                                        >
                                                                          <Box
                                                                            sx={{
                                                                              display:
                                                                                "flex",
                                                                              justifyContent:
                                                                                "space-between",
                                                                              alignItems:
                                                                                "flex-start",
                                                                            }}
                                                                          >
                                                                            <Box
                                                                              sx={{
                                                                                flex: 1,
                                                                              }}
                                                                            >
                                                                              <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                  display:
                                                                                    "block",
                                                                                  color:
                                                                                    "#39f500ff",
                                                                                  fontWeight:
                                                                                    "medium",
                                                                                  fontSize:
                                                                                    "0.8rem",
                                                                                }}
                                                                              >
                                                                                {template.Name ||
                                                                                  template.TemplateName ||
                                                                                  template.templateName ||
                                                                                  `Template ${
                                                                                    index +
                                                                                    1
                                                                                  }`}
                                                                              </Typography>
                                                                              {template.Description && (
                                                                                <Typography
                                                                                  variant="caption"
                                                                                  sx={{
                                                                                    display:
                                                                                      "block",
                                                                                    mt: 0.5,
                                                                                    color:
                                                                                      "#666",
                                                                                    fontStyle:
                                                                                      "italic",
                                                                                    fontSize:
                                                                                      "0.75rem",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    template.Description
                                                                                  }
                                                                                </Typography>
                                                                              )}
                                                                            </Box>

                                                                            {/* View button if PDF file is available */}
                                                                            {template.Listfile && (
                                                                              <Button
                                                                                variant="outlined"
                                                                                size="small"
                                                                                color="primary"
                                                                                onClick={() => {
                                                                                  handleOpenPdfViewer(
                                                                                    template.Listfile,
                                                                                    template.FormatName ||
                                                                                      "Document",
                                                                                  );
                                                                                }}
                                                                                sx={{
                                                                                  ml: 1,
                                                                                  minWidth:
                                                                                    "auto",
                                                                                  px: 1,
                                                                                  fontSize:
                                                                                    "0.7rem",
                                                                                  textTransform:
                                                                                    "none",
                                                                                }}
                                                                              >
                                                                                View
                                                                                PDF
                                                                              </Button>
                                                                            )}
                                                                          </Box>

                                                                          {/* Show clause info for debugging */}
                                                                          <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                              display:
                                                                                "block",
                                                                              mt: 0.5,
                                                                              color:
                                                                                "#999",
                                                                              fontSize:
                                                                                "0.7rem",
                                                                            }}
                                                                          >
                                                                            Clause:{" "}
                                                                            {
                                                                              template.clauseno
                                                                            }{" "}
                                                                            |
                                                                            ID:{" "}
                                                                            {
                                                                              template.id
                                                                            }
                                                                          </Typography>
                                                                        </Box>
                                                                      ),
                                                                    )}
                                                                  </Box>
                                                                )}

                                                                {/* Fallback to static templates if no dynamic templates */}
                                                                {availableTemplates.length ===
                                                                  0 &&
                                                                  requirement.templates && (
                                                                    <Typography
                                                                      variant="caption"
                                                                      sx={{
                                                                        display:
                                                                          "block",
                                                                        mt: 1,
                                                                        color:
                                                                          "#666",
                                                                        fontStyle:
                                                                          "italic",
                                                                      }}
                                                                    >
                                                                      {
                                                                        requirement.templates
                                                                      }
                                                                    </Typography>
                                                                  )}
                                                              </Box>
                                                            );
                                                          }
                                                          return null;
                                                        })()}
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{
                                                          minWidth: 200,
                                                          p: 2,
                                                        }}
                                                      >
                                                        {getNcOptionsForRequirement(
                                                          requirement,
                                                        ).length > 0 ? (
                                                          <>
                                                            <Typography
                                                              variant="caption"
                                                              sx={{
                                                                fontWeight:
                                                                  "bold",
                                                                color:
                                                                  "#1976d2",
                                                                mb: 1,
                                                                display:
                                                                  "block",
                                                              }}
                                                            >
                                                              Non-Compliance
                                                              Items:
                                                            </Typography>
                                                            <FormGroup>
                                                              {getNcOptionsForRequirement(
                                                                requirement,
                                                              ).map(
                                                                (
                                                                  ncOption,
                                                                  index,
                                                                ) => {
                                                                  const isChecked =
                                                                    (
                                                                      ncSelections.get(
                                                                        requirement.id,
                                                                      ) ||
                                                                      new Set()
                                                                    ).has(
                                                                      ncOption.id,
                                                                    );
                                                                  const clauseNo =
                                                                    requirement.clause;
                                                                  const hasEvidenceData =
                                                                    hasEvidence(
                                                                      ncOption.text,
                                                                      clauseNo,
                                                                    );

                                                                  return (
                                                                    <Box
                                                                      key={
                                                                        index
                                                                      }
                                                                      sx={{
                                                                        mb: 1,
                                                                      }}
                                                                    >
                                                                      <FormControlLabel
                                                                        control={
                                                                          <Checkbox
                                                                            checked={
                                                                              isChecked
                                                                            }
                                                                            onChange={(
                                                                              e,
                                                                            ) =>
                                                                              handleNcChange(
                                                                                requirement.id,
                                                                                ncOption.id,
                                                                                e
                                                                                  .target
                                                                                  .checked,
                                                                              )
                                                                            }
                                                                            disabled={
                                                                              isChecklistCompleted
                                                                            }
                                                                            size="small"
                                                                            sx={{
                                                                              color:
                                                                                "#f44336",
                                                                              "&.Mui-checked":
                                                                                {
                                                                                  color:
                                                                                    "#f44336",
                                                                                },
                                                                            }}
                                                                          />
                                                                        }
                                                                        label={
                                                                          <Typography variant="caption">
                                                                            {
                                                                              ncOption.text
                                                                            }
                                                                          </Typography>
                                                                        }
                                                                        sx={{
                                                                          flex: 1,
                                                                        }}
                                                                      />
                                                                      {isChecked && (
                                                                        <div
                                                                          style={{
                                                                            display:
                                                                              "flex",
                                                                            alignItems:
                                                                              "center",
                                                                          }}
                                                                        >
                                                                          <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            color={
                                                                              hasEvidenceData
                                                                                ? "success"
                                                                                : "error"
                                                                            }
                                                                            onClick={() =>
                                                                              handleEvidenceModalOpen(
                                                                                ncOption.text,
                                                                                ncOption.id,
                                                                                clauseNo,
                                                                              )
                                                                            }
                                                                            startIcon={
                                                                              hasEvidenceData ? (
                                                                                <CheckCircle />
                                                                              ) : (
                                                                                <CloudUpload />
                                                                              )
                                                                            }
                                                                            sx={{
                                                                              ml: 1,
                                                                              minWidth:
                                                                                "auto",
                                                                              px: 1.5,
                                                                              py: 0.5,
                                                                              fontSize:
                                                                                "0.7rem",
                                                                              textTransform:
                                                                                "none",
                                                                              fontWeight:
                                                                                "bold",
                                                                              borderRadius: 2,
                                                                              boxShadow: 2,
                                                                              "&:hover":
                                                                                {
                                                                                  boxShadow: 4,
                                                                                  transform:
                                                                                    "translateY(-1px)",
                                                                                },
                                                                              transition:
                                                                                "all 0.2s ease",
                                                                            }}
                                                                          >
                                                                            {hasEvidenceData
                                                                              ? "View Details"
                                                                              : "Upload Evidence"}
                                                                          </Button>
                                                                        </div>
                                                                      )}
                                                                    </Box>
                                                                  );
                                                                },
                                                              )}
                                                            </FormGroup>
                                                          </>
                                                        ) : null}
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{
                                                          minWidth: 200,
                                                          p: 2,
                                                        }}
                                                      >
                                                        {(() => {
                                                          const clauseNo =
                                                            requirement.clause;
                                                          const existingRemark =
                                                            getExistingRemarkForClause(
                                                              clauseNo,
                                                            );
                                                          const hasExisting =
                                                            hasExistingRemark(
                                                              clauseNo,
                                                            );
                                                          const ncOptions =
                                                            getNcOptionsForRequirement(
                                                              requirement,
                                                            );
                                                          const hasNoNcOptions =
                                                            ncOptions.length ===
                                                            0;

                                                          // Don't show remarks field if no NC options are available
                                                          if (hasNoNcOptions) {
                                                            return null;
                                                          }

                                                          if (
                                                            hasExisting &&
                                                            existingRemark
                                                          ) {
                                                            return (
                                                              <Alert
                                                                severity="info"
                                                                sx={{
                                                                  backgroundColor:
                                                                    "#e3f2fd",
                                                                  border:
                                                                    "1px solid #2196f3",
                                                                }}
                                                              >
                                                                <Typography
                                                                  variant="body2"
                                                                  sx={{
                                                                    fontWeight:
                                                                      "medium",
                                                                  }}
                                                                >
                                                                  Existing
                                                                  Remark:
                                                                </Typography>
                                                                <Typography
                                                                  variant="body2"
                                                                  sx={{
                                                                    mt: 0.5,
                                                                  }}
                                                                >
                                                                  {
                                                                    existingRemark.Remark
                                                                  }
                                                                </Typography>
                                                              </Alert>
                                                            );
                                                          } else {
                                                            const remarkValue =
                                                              remarks.get(
                                                                requirement.id,
                                                              ) || "";

                                                            return (
                                                              <Box>
                                                                <Button
                                                                  variant="outlined"
                                                                  size="small"
                                                                  onClick={() =>
                                                                    handleOpenFeedbackDialog(
                                                                      requirement,
                                                                    )
                                                                  }
                                                                  disabled={
                                                                    isChecklistCompleted
                                                                  }
                                                                  startIcon={
                                                                    <Lightbulb />
                                                                  }
                                                                  fullWidth
                                                                  sx={{
                                                                    textTransform:
                                                                      "none",
                                                                    borderRadius: 1,
                                                                    py: 1,
                                                                    borderColor:
                                                                      remarkValue
                                                                        ? "#1976d2"
                                                                        : "#ccc",
                                                                    color:
                                                                      remarkValue
                                                                        ? "#1976d2"
                                                                        : "#666",
                                                                    fontWeight:
                                                                      remarkValue
                                                                        ? "bold"
                                                                        : "normal",
                                                                    "&:hover": {
                                                                      borderColor:
                                                                        "#1976d2",
                                                                      backgroundColor:
                                                                        "#f5f5f5",
                                                                    },
                                                                  }}
                                                                >
                                                                  {remarkValue
                                                                    ? "View/Edit Remark/Observation/Suggestion"
                                                                    : "Add Remark/Observation/Suggestion on content of checklist for NABL"}
                                                                </Button>
                                                                {remarkValue && (
                                                                  <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                      mt: 0.5,
                                                                      display:
                                                                        "block",
                                                                      whiteSpace:
                                                                        "nowrap",
                                                                      overflow:
                                                                        "hidden",
                                                                      textOverflow:
                                                                        "ellipsis",
                                                                    }}
                                                                  >
                                                                    {remarkValue.substring(
                                                                      0,
                                                                      50,
                                                                    )}
                                                                    {remarkValue.length >
                                                                    50
                                                                      ? "..."
                                                                      : ""}
                                                                  </Typography>
                                                                )}
                                                              </Box>
                                                            );
                                                          }
                                                        })()}
                                                      </TableCell>
                                                    </TableRow>
                                                  ),
                                                )}

                                              {/* Load More Questions Button */}
                                              {hasMoreQuestions && (
                                                <TableRow>
                                                  <TableCell
                                                    colSpan={4}
                                                    sx={{
                                                      textAlign: "center",
                                                      py: 2,
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        gap: 1,
                                                        justifyContent:
                                                          "center",
                                                        alignItems: "center",
                                                      }}
                                                    >
                                                      <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => {
                                                          console.log(
                                                            `Load More clicked for ${sectionId}: current=${currentLimit}, total=${totalQuestions}`,
                                                          );
                                                          loadMoreQuestions(
                                                            sectionId,
                                                            totalQuestions,
                                                          );
                                                        }}
                                                        startIcon={
                                                          <ExpandMore />
                                                        }
                                                      >
                                                        Load 5 More Questions
                                                      </Button>

                                                      {totalQuestions -
                                                        currentLimit >
                                                        5 && (
                                                        <Button
                                                          variant="text"
                                                          size="small"
                                                          onClick={() => {
                                                            console.log(
                                                              `Load All clicked for ${sectionId}: current=${currentLimit}, total=${totalQuestions}`,
                                                            );
                                                            loadAllQuestions(
                                                              sectionId,
                                                              totalQuestions,
                                                            );
                                                          }}
                                                          sx={{ ml: 1 }}
                                                        >
                                                          Load All (
                                                          {totalQuestions -
                                                            currentLimit}{" "}
                                                          remaining)
                                                        </Button>
                                                      )}
                                                    </Box>
                                                  </TableCell>
                                                </TableRow>
                                              )}

                                              {/* Performance Info */}
                                              {totalQuestions > 3 && (
                                                <TableRow>
                                                  <TableCell
                                                    colSpan={4}
                                                    sx={{
                                                      textAlign: "center",
                                                      py: 1,
                                                      backgroundColor:
                                                        "#f8f9fa",
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      Showing{" "}
                                                      {Math.min(
                                                        currentLimit,
                                                        totalQuestions,
                                                      )}{" "}
                                                      of {totalQuestions}{" "}
                                                      questions for better
                                                      performance
                                                      {currentLimit >=
                                                        totalQuestions &&
                                                        totalQuestions > 3 && (
                                                          <span
                                                            style={{
                                                              color: "#4caf50",
                                                              fontWeight:
                                                                "bold",
                                                            }}
                                                          >
                                                            {" "}
                                                            • All questions
                                                            loaded
                                                          </span>
                                                        )}
                                                    </Typography>
                                                  </TableCell>
                                                </TableRow>
                                              )}
                                            </>
                                          );
                                        })()}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </Card>
                      ))}
                  </AccordionDetails>
                </Accordion>
              </Card>
            ))}

        {/* Read-Only Banner - Show when checklist is completed */}
        {isChecklistCompleted && (
          <Alert
            severity="success"
            icon={<CheckCircle />}
            sx={{
              mt: 3,
              mb: 2,
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(46, 125, 50, 0.2)",
              border: "2px solid #4caf50",
              "& .MuiAlert-icon": {
                fontSize: 32,
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Checklist Completed - View Only Mode
            </Typography>
            <Typography variant="body2">
              This checklist was completed on <strong>{completionDate}</strong>.
              You can view all details but cannot make any changes or create new
              non-conformities.
            </Typography>
          </Alert>
        )}

        {/* Complete Checklist Button - Hide when already completed */}
        {!isChecklistCompleted && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleOpenCompletionDialog}
              startIcon={<AssignmentTurnedIn />}
              sx={{
                px: 6,
                py: 2,
                fontSize: "1.1rem",
                fontWeight: "bold",
                borderRadius: 3,
                background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                boxShadow: "0 4px 20px rgba(25, 118, 210, 0.4)",
                textTransform: "none",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                  boxShadow: "0 6px 25px rgba(25, 118, 210, 0.5)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Complete Checklist Review
            </Button>
          </Box>
        )}

        {/* Completion Confirmation Dialog */}
        <Dialog
          open={completionDialog.open}
          onClose={handleCloseCompletionDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 2,
              pt: 3,
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              color: "white",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <AssignmentTurnedIn sx={{ fontSize: 32 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Complete Checklist Review
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: "medium", mb: 1 }}>
                I have reviewed the complete checklist of {labType=='Medical' ? 'ISO 15189' : 'ISO/IEC 17025'} & clauses
                for which I have not marked Non Conformity are considered as
                adequate.
              </Typography>
            </Alert>
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Are you sure?
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Post this you will not be able to edit or Create Non
                conformities.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={handleCloseCompletionDialog}
              variant="outlined"
              disabled={completionDialog.loading}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              No, Cancel
            </Button>
            <Button
              onClick={handleConfirmCompletion}
              variant="contained"
              disabled={completionDialog.loading}
              startIcon={
                completionDialog.loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircle />
                )
              }
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: "bold",
                background: "linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
                },
              }}
            >
              {completionDialog.loading ? "Processing..." : "Yes, Complete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Evidence Modal */}
        <Dialog
          open={evidenceModal.open}
          onClose={handleEvidenceModalClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h6" color="error" fontWeight="bold">
                  📋 Non-Compliance Evidence
                </Typography>
                {/* <Typography variant="body2" color="text.secondary">
                  {evidenceModal.ncItem}
                </Typography> */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="listform-html-content"
                  dangerouslySetInnerHTML={{
                    __html: evidenceModal.ncItem,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Clause: {evidenceModal.clauseNo}
                </Typography>
              </Box>
              <IconButton onClick={handleEvidenceModalClose} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {/* Available Images Section */}
            {getEvidenceForClause(evidenceModal.clauseNo).images.length > 0 && (
              <Card
                variant="outlined"
                sx={{ mb: 3, p: 3, backgroundColor: "#e3f2fd" }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  🖼️ Available/Selected Images (Clause {evidenceModal.clauseNo})
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Click on images to select them as evidence.
                </Typography>
                <Grid container spacing={2}>
                  {getEvidenceForClause(evidenceModal.clauseNo).images.map(
                    (evidence, index) => {
                      const isSelected =
                        evidenceModal.selectedImages?.includes(
                          evidence.signedUrl,
                        ) || false;
                      return (
                        <Grid
                          item
                          xs={6}
                          sm={4}
                          md={3}
                          key={evidence.id || index}
                        >
                          <Card
                            sx={{
                              cursor: isChecklistCompleted
                                ? "default"
                                : "pointer",
                              border: isSelected
                                ? "3px solid #1976d2"
                                : "1px solid #ddd",
                              borderRadius: 2,
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: isChecklistCompleted
                                  ? "none"
                                  : "scale(1.05)",
                                boxShadow: 3,
                              },
                              position: "relative",
                              opacity: isChecklistCompleted ? 0.7 : 1,
                            }}
                            onClick={() => {
                              if (!isChecklistCompleted) {
                                setEvidenceModal((prev) => ({
                                  ...prev,
                                  selectedImages: isSelected
                                    ? (prev.selectedImages || []).filter(
                                        (url) => url !== evidence.signedUrl,
                                      )
                                    : [
                                        ...(prev.selectedImages || []),
                                        evidence.signedUrl,
                                      ],
                                }));
                              }
                            }}
                          >
                            {isSelected && (
                              <Chip
                                label="Selected"
                                color="primary"
                                size="small"
                                icon={<CheckCircle />}
                                sx={{
                                  position: "absolute",
                                  top: 5,
                                  right: 5,
                                  zIndex: 1,
                                  fontWeight: "bold",
                                }}
                              />
                            )}
                            <img
                              src={evidence.signedUrl}
                              alt={`Evidence ${index + 1}`}
                              style={{
                                width: "100%",
                                height: "120px",
                                objectFit: "cover",
                                borderRadius: "8px",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                            <Box
                              sx={{
                                display: "none",
                                height: "120px",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f5f5f5",
                                borderRadius: "8px",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Image {index + 1}
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      );
                    },
                  )}
                </Grid>
              </Card>
            )}

            {/* Available Documents Section (PDF/Excel) */}
            {getEvidenceForClause(evidenceModal.clauseNo).documents.length >
              0 && (
              <Card
                variant="outlined"
                sx={{ mb: 3, p: 3, backgroundColor: "#fff3e0" }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  📄 Available/Selected Documents (Clause{" "}
                  {evidenceModal.clauseNo})
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Click on documents to select them as evidence.
                </Typography>
                <Grid container spacing={2}>
                  {getEvidenceForClause(evidenceModal.clauseNo).documents.map(
                    (evidence, index) => {
                      const isSelected =
                        evidenceModal.selectedDocuments?.includes(
                          evidence.fileName,
                        ) || false;
                      const fileExt = evidence.fileName
                        .split(".")
                        .pop()
                        .toLowerCase();
                      const isPdf = fileExt === "pdf";
                      const isExcel = fileExt === "xlsx" || fileExt === "xls";

                      return (
                        <Grid item xs={12} sm={6} key={evidence.id || index}>
                          <Card
                            sx={{
                              cursor: isChecklistCompleted
                                ? "default"
                                : "pointer",
                              border: isSelected
                                ? "3px solid #ff9800"
                                : "1px solid #ddd",
                              borderRadius: 2,
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: isChecklistCompleted
                                  ? "none"
                                  : "scale(1.02)",
                                boxShadow: 3,
                              },
                              position: "relative",
                              p: 2,
                              opacity: isChecklistCompleted ? 0.7 : 1,
                            }}
                            onClick={(e) => {
                              // Prevent card click if clicking on view button
                              if (e.target.closest(".view-document-button")) {
                                return;
                              }

                              // Open file in browser if Ctrl/Cmd key is pressed or if checklist is completed
                              if (
                                e.ctrlKey ||
                                e.metaKey ||
                                isChecklistCompleted
                              ) {
                                if (evidence.signedUrl) {
                                  window.open(evidence.signedUrl, "_blank");
                                }
                                return;
                              }

                              // Normal selection behavior
                              if (!isChecklistCompleted) {
                                setEvidenceModal((prev) => ({
                                  ...prev,
                                  selectedDocuments: isSelected
                                    ? (prev.selectedDocuments || []).filter(
                                        (name) => name !== evidence.fileName,
                                      )
                                    : [
                                        ...(prev.selectedDocuments || []),
                                        evidence.fileName,
                                      ],
                                }));
                              }
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 60,
                                  height: 60,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: isPdf
                                    ? "#e53935"
                                    : isExcel
                                      ? "#43a047"
                                      : "#757575",
                                  borderRadius: 2,
                                  color: "white",
                                  fontSize: "24px",
                                  fontWeight: "bold",
                                }}
                              >
                                {isPdf ? "📕" : isExcel ? "📊" : "📄"}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight="600">
                                  {evidence.fileName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {fileExt.toUpperCase()} Document
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <Tooltip title="View Document">
                                  <IconButton
                                    className="view-document-button"
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (evidence.signedUrl) {
                                        window.open(
                                          evidence.signedUrl,
                                          "_blank",
                                        );
                                      }
                                    }}
                                    sx={{
                                      backgroundColor: "#1976d2",
                                      color: "white",
                                      "&:hover": {
                                        backgroundColor: "#1565c0",
                                      },
                                    }}
                                  >
                                    <Visibility sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </Tooltip>
                                {isSelected && (
                                  <CheckCircle
                                    sx={{ color: "#ff9800", fontSize: 28 }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      );
                    },
                  )}
                </Grid>
              </Card>
            )}

            {/* Selected Images Section - Commented Out */}

            {/* Remarks Section */}
            <Card
              variant="outlined"
              sx={{ mb: 3, p: 3, backgroundColor: "#f8f9fa" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", mb: 2 }}
              >
                📝 Additional Remarks
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Provide detailed remarks, observations, or additional context
                for this non-compliance.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                placeholder="Enter detailed remarks about this non-compliance item..."
                value={evidenceModal.notes}
                onChange={(e) =>
                  setEvidenceModal((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                disabled={isChecklistCompleted}
                InputProps={{
                  readOnly: isChecklistCompleted,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                💡 Tip: Include specific details about the non-compliance, its
                impact, and any corrective actions needed.
              </Typography>
            </Card>

            {/* Status Messages */}
            {evidenceModal.uploadStatus && (
              <Alert
                severity={
                  evidenceModal.uploadStatus.includes("Error")
                    ? "error"
                    : "success"
                }
                sx={{ mb: 2 }}
              >
                {evidenceModal.uploadStatus}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={handleEvidenceModalClose}
              variant="outlined"
              sx={{ mr: 2 }}
            >
              {isChecklistCompleted ? "Close" : "Cancel"}
            </Button>
            {!isChecklistCompleted && (
              <Button
                onClick={handleSaveEvidence}
                variant="contained"
                disabled={
                  !evidenceModal.file &&
                  !evidenceModal.notes.trim() &&
                  (!evidenceModal.selectedImages ||
                    evidenceModal.selectedImages.length === 0)
                }
                startIcon={
                  uploading ? <CircularProgress size={16} /> : <Save />
                }
                sx={{
                  px: 3,
                  py: 1,
                  fontWeight: "bold",
                  borderRadius: 2,
                  boxShadow: 2,
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {uploading ? "Uploading..." : "Save Evidence & Remarks"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Location Warning Dialog */}
        <LocationWarningDialog
          open={locationWarningOpen}
          onClose={() => setLocationWarningOpen(false)}
          distanceMessage={distanceMessage}
        />

        {/* Hourly Attendance Dialog */}
        <HourlyAttendanceDialog
          open={attendanceCheckDialogOpen}
          onClose={() => setAttendanceCheckDialogOpen(false)}
          message={attendanceCheckMessage}
          onRecheck={checkHourlyAttendance}
        />

        {/* Feedback Dialog */}
        <Dialog
          open={feedbackDialog.open}
          onClose={handleCloseFeedbackDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: 5,
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              color: "white",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Lightbulb />
              <span>
                Remark/Observation/Suggestion on checklist content for NABL
              </span>
            </Box>
            <IconButton
              onClick={handleCloseFeedbackDialog}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ mt: 3 }}>
            {/* <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                This Remark/Observation/Suggestion will not be considered as part of the report. Use
                this field to provide internal notes or suggestions for NABL.
              </Typography>
            </Alert> */}

            {/* Feedback TextField */}
            <TextField
              fullWidth
              multiline
              rows={6}
              variant="outlined"
              placeholder="Enter your feedback here..."
              value={feedbackDialog.currentFeedback}
              onChange={(e) => handleFeedbackTextChange(e.target.value)}
              disabled={isChecklistCompleted}
              InputProps={{
                readOnly: isChecklistCompleted,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                  },
                },
              }}
            />
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={handleCloseFeedbackDialog}
              variant="outlined"
              sx={{ mr: 2 }}
            >
              {isChecklistCompleted ? "Close" : "Cancel"}
            </Button>
            {!isChecklistCompleted && (
              <Button
                onClick={handleSaveFeedback}
                variant="contained"
                startIcon={<Save />}
                sx={{
                  px: 3,
                  py: 1,
                  fontWeight: "bold",
                  borderRadius: 2,
                  boxShadow: 2,
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Save Feedback
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* NC Checklist Dialog */}
        <Dialog
          open={ncChecklistDialog.open}
          onClose={handleCloseNCChecklistDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
              color: "#fff",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Info />
            Application Finding for Clause {ncChecklistDialog.clauseNo}
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 2 }}>
            {ncChecklistDialog.matchedRecords.length === 0 ? (
              <Typography variant="body1" color="text.secondary">
                No NC records found for this clause.
              </Typography>
            ) : (
              <Box>
                {ncChecklistDialog.matchedRecords.map((record, index) => {
                  // Split clause numbers and observations
                  const clauseNumbers = (record.clauseNo || "")
                    .split(",")
                    .map((c) => c.trim());
                  const observations = (record.Observation || "")
                    .split("~")
                    .map((o) => o.trim());

                  // Find the index of the clicked clause
                  const clickedClauseIndex = clauseNumbers.indexOf(
                    ncChecklistDialog.clauseNo,
                  );

                  // Get the corresponding observation for the clicked clause
                  const relevantObservation =
                    clickedClauseIndex !== -1 &&
                    clickedClauseIndex < observations.length
                      ? observations[clickedClauseIndex]
                      : record.Observation; // Fallback to full observation if split fails

                  return (
                    <Card
                      key={index}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        "&:hover": {
                          boxShadow: 3,
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Section: {record.SectionName}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Clause No:</strong> {ncChecklistDialog.clauseNo}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Observation:</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          backgroundColor: "#f5f5f5",
                          p: 2,
                          borderRadius: 1,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {relevantObservation}
                      </Typography>
                      {record.Insertdate && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          Date: {new Date(record.Insertdate).toLocaleString()}
                        </Typography>
                      )}
                    </Card>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseNCChecklistDialog} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* PDF Viewer Dialog */}
        <PreviewReportPDFViewer
          open={pdfViewer.open}
          onClose={handleClosePdfViewer}
          pdfUrl={pdfViewer.url}
          reportTitle={pdfViewer.title}
          disableDownload={true}
        />
      </Container>
    </>
  );
};

export default Checklist;